// Top-level orchestrator. Wires PlayCanvas, room/variant state, hotspots, and UI.

import { LoadingScreen } from '../ui/loadingScreen';
import { ChromeMetrics } from '../ui/chromeMetrics';
import { ALL_DICTS, getT, type Locale, type TFn } from '../i18n';
import { activateMenuItem, buildMenuVoiceIndex } from '../ui/voiceMenu';
import { applyDocumentLocale, resolveLocale, setStoredLocale } from '../i18n/resolveLocale';
import { applyLocale, localizeConfig, type LocalizedConfigBinding } from './localizeConfig';
import { MenuHost } from '../ui/menu/menuHost';
import { buildMenu, legacyMenuItems, operatorMenuItems } from '../ui/menu/registry';
import { resolveAudience } from '../ui/menu/audience';
import { isCoarsePointer } from '../ui/menu/capabilities';
import type { MenuContext } from '../ui/menu/types';
import { Ui } from '../ui/ui';
import { ModalRoot } from '../ui/modal';
import { MaterialPanel } from '../ui/materialPanel';
import { EditorPanel } from '../ui/editorPanel';
import { EditorMode } from './editorMode';
import { SunPanel } from '../ui/sunPanel';
import { openInfoModal } from '../ui/infoModal';
import { openCtaModal } from '../ui/ctaPanel';
import { AudioControls } from '../ui/audioControls';
import { DebugLook } from '../ui/debugLook';
import { Dashboard } from '../ui/dashboard';
import { showToast } from '../ui/toast';
import { VrButton } from '../ui/vrButton';
import { MaterialOverlay } from '../playcanvas/materialOverlay';
import { NotesManager } from './notesManager';
import { ChatPanel } from '../ui/chatPanel';
import type { TourReportData } from '../ui/tourReport';
import * as pc from 'playcanvas';
import { vec3ToYawPitch } from '../playcanvas/mathUtils';
import { HOTSPOT_DEFAULT_DISTANCE } from './config';
import { createApp } from '../playcanvas/createApp';
import { XrManager } from '../playcanvas/xrManager';
import { InfoCard3D } from '../playcanvas/infoCard3D';
import { IdleOrbit } from '../playcanvas/idleOrbit';
import { TiltControls } from '../playcanvas/tiltControls';
import { QrPanel } from '../ui/qrPanel';
import { Narrator } from '../ui/narrator';
import { VoiceCommands } from '../ui/voiceCommands';
import { CloudVoiceInput } from '../ui/cloudVoiceInput';
import { AutoTour } from '../ui/autoTour';
import { VariantCompare } from '../ui/variantCompare';
import { SavedSessions } from '../ui/savedSessions';
import { VariantsGallery } from '../ui/variantsGallery';
import { Copresence, type RemoteActionInput } from '../ui/copresence';
import type { BuyerSession } from './types';
import { CameraControls } from '../playcanvas/cameraControls';
import { AssetLoader } from '../playcanvas/assetLoader';
import { FadeTransition } from '../playcanvas/fadeTransition';
import { RoomManager } from '../playcanvas/roomManager';
import { VariantManager } from '../playcanvas/variantManager';
import { HotspotManager, type HotspotSelectEvent } from '../playcanvas/hotspotManager';
import { MeasureTool } from '../playcanvas/measureTool';
import { Dollhouse } from '../playcanvas/dollhouse';
import { openVideoModal } from '../playcanvas/videoPlane';
import { getAnalytics, type Analytics } from '../playcanvas/analytics';
import { PanoramaRenderer } from '../playcanvas/panoramaRenderer';
import { GsplatRenderer } from '../playcanvas/gsplatRenderer';
import { GltfRenderer } from '../playcanvas/gltfRenderer';
import { rendererKindFor, type RoomRenderer } from '../playcanvas/roomRenderer';
import { SunSystem, type SunState } from '../playcanvas/sunSystem';
import {
  applyInitialConfigOverrides,
  parseUrlState,
  UrlStateSync,
  wrapYawDeg,
  type ParsedUrlState,
} from './urlStateSync';
import type { ApartmentInfo, FaqEntry, Hotspot, InfoHotspot, NoteHotspot, Room, RoomsConfig, SurfaceHotspot, Variant } from './types';

/**
 * Shape of the context block built by `App.buildChatContext` and sent to
 * `/api/chat`. Kept colocated here (rather than in types.ts) because every
 * field is purely chat-presentation state, not part of the rooms.json
 * schema. ChatPanelDeps.getContext returns this exact shape.
 */
export interface ChatContext {
  currentRoomId: string;
  currentRoomName?: string;
  currentRoomSubtitle?: string;
  currentVariantName?: string;
  currentRoomDescription?: string;
  currentRoomCeilingM?: number;
  currentRoomAreaM2?: number;
  currentRoomWindowsM2?: number;
  currentRoomSolarOrientation?: string;
  currentFeatures?: string[];
  currentInfoPoints: Array<{ label: string; description: string }>;
  currentMaterials: Array<{
    surfaceLabel: string;
    surfaceAreaM2?: number;
    finishes: Array<{ label: string; pricePerSqM?: number }>;
  }>;
  currentVariantOptions: string[];
  otherRooms: Array<{
    id: string;
    name: string;
    subtitle?: string;
    description?: string;
    ceilingHeightM?: number;
    areaM2?: number;
    variants?: string[];
  }>;
  branding?: { name?: string; subtitle?: string };
  apartment?: ApartmentInfo;
  faq?: FaqEntry[];
  cta?: { contactUrl?: string; defaultLabel?: string };
}

/** Shoelace area for a closed polygon in apartment-frame metres. Returns
 *  rounded m² so the chat reads "24 m²", not "23.9871 m²". Undefined when
 *  the polygon is missing or degenerate. */
function computePolygonAreaM2(shape: Array<[number, number]> | undefined): number | undefined {
  if (!shape || shape.length < 3) return undefined;
  let sum = 0;
  for (let i = 0; i < shape.length; i++) {
    const [x1, y1] = shape[i]!;
    const [x2, y2] = shape[(i + 1) % shape.length]!;
    sum += x1 * y2 - x2 * y1;
  }
  const area = Math.abs(sum) / 2;
  return area > 0 ? Math.round(area) : undefined;
}
import {
  rehydrate as rehydrateGeneratedVariants,
  getProjectId,
  setProjectId,
  importFromServer,
} from './generatedVariantStore';
import type { TourConfigChangeReason, TourEmbedBridge } from './tourEmbed';
import roomsData from '../data/rooms.json';

export interface AppOptions {
  canvas: HTMLCanvasElement;
  /**
   * Demo Studio embed — the RoomsConfig the portal pushed over postMessage.
   * When absent (every non-embedded boot) the bundled src/data/rooms.json is
   * used, exactly as before.
   */
  config?: RoomsConfig;
  /**
   * Overrides the viewer's own locale resolution. Set only in ?embed=1 mode,
   * from the parent's `locale` on the config message: the editor iframe is
   * created once and the admin can change the portal's language without it
   * being recreated, so `?lang=` in its src would go stale.
   */
  locale?: Locale;
  /** Set only in ?embed=1 mode; the channel edits are reported back on. */
  embed?: TourEmbedBridge | null;
}

export class App {
  private readonly canvas: HTMLCanvasElement;
  private readonly chromeMetrics = new ChromeMetrics();
  /** Resolved once at construction; the menu and (Phase 5) the VR panel read it. */
  /** Assigned in the constructor — an embed's parent may override it. */
  private locale: Locale;
  /** Assigned in the constructor, right after `locale` — a field initialiser
   *  here would run BEFORE the constructor body set the locale it reads. */
  private t: TFn;
  private menu: MenuHost | null = null;
  /** Every localisable field of `config`, with the value it was authored with.
   *  Assigned in the constructor, so it is never null after construction. */
  private readonly localizedConfig: LocalizedConfigBinding;
  /** Both are operator overlays the menu drives; they mount after installMenu(),
   *  so their entries reach them lazily rather than capturing an instance. */
  private dashboard: Dashboard | null = null;
  private debugLook: DebugLook | null = null;
  private readonly loading: LoadingScreen;
  private readonly analytics: Analytics;
  private readonly config: RoomsConfig;

  private appHandle: ReturnType<typeof createApp> | null = null;
  private panoRenderer: PanoramaRenderer | null = null;
  private gsplatRenderer: GsplatRenderer | null = null;
  private gltfRenderer: GltfRenderer | null = null;
  private currentRenderer: RoomRenderer | null = null;
  private controls: CameraControls | null = null;
  private assets: AssetLoader | null = null;
  private fade: FadeTransition | null = null;
  private rooms: RoomManager | null = null;
  private variants: VariantManager | null = null;
  private hotspots: HotspotManager | null = null;
  private measure: MeasureTool | null = null;
  private dollhouse: Dollhouse | null = null;
  private modal: ModalRoot | null = null;
  private audio: AudioControls | null = null;
  private narrator: Narrator | null = null;
  private voiceCommands: VoiceCommands | null = null;
  /** Cloud STT (Groq Whisper) for VR push-to-talk — Web Speech is dead on Quest. */
  private cloudVoice: CloudVoiceInput | null = null;
  /** Speakable menu names in every language, built in installMenu(). Empty
   *  until then, which is fine — voice cannot fire before the menu mounts. */
  private menuVoiceIndex: ReturnType<typeof buildMenuVoiceIndex> = [];
  /** In-flight CloudVoiceInput.start() promise — awaited on grip release so a
   *  quick tap still records (fixes the start/stop race). */
  private vrVoiceStart: Promise<boolean> | null = null;
  /** WebAudio context for VR push-to-talk beeps (DOM/toasts are invisible in VR). */
  private cueCtx: AudioContext | null = null;
  private savedSessions: SavedSessions | null = null;
  private variantsGallery: VariantsGallery | null = null;
  /** True while a `?s=` session is being applied — suppresses autosave so we
   *  don't immediately re-save the state we just loaded. */
  private hydratingSession = false;
  /** Debounce handle for background session autosave. */
  private autosaveTimer: ReturnType<typeof setTimeout> | null = null;
  private materialPanel: MaterialPanel | null = null;
  /** Sprint 3 Slice 1 — only set when ?edit=1 is in the URL. */
  private editorMode: EditorMode | null = null;
  private editorPanel: EditorPanel | null = null;
  /** Demo Studio — set only in ?embed=1 mode. Every config mutation is
   *  reported through it; the portal is what actually persists. */
  private readonly embed: TourEmbedBridge | null;
  /**
   * Phase 3 scaffold — single-panorama material swap. Lazily instantiated
   * the first time a buyer opens a surface hotspot that declares a maskUrl
   * + materials. Stays alive across room changes (the texture cache is
   * released on room change to keep GPU memory flat).
   */
  private materialOverlay: MaterialOverlay | null = null;
  /** Active material-finish id for aria-pressed in the surface panel; null while no finish is applied. */
  private currentMaterialFinishId: string | null = null;

  /** Buyer-authored sticky notes (Phase 8 comments). Lives in App, not
   *  rooms.json — survives `?s=<code>` reload via BuyerSession.notes. */
  private notes: NotesManager | null = null;
  /** When true, the next canvas tap drops a note at the picked yaw/pitch
   *  instead of running the normal hotspot picker. Toggled by the 📝 button. */
  private notePlacementArmed = false;
  /** In-tour LLM chat assistant. Kept as a field (rather than fire-and-forget
   *  install) so applyBuyerSession can re-key its localStorage scope when a
   *  `?s=<code>` URL is hydrated — prevents one buyer's chat from leaking to
   *  the next buyer on a shared device. Same pattern as NotesManager. */
  private chatPanel: ChatPanel | null = null;
  private ui: Ui | null = null;
  private xr: XrManager | null = null;
  private infoCard3D: InfoCard3D | null = null;
  private copresence: Copresence | null = null;
  /** True while a hotspot modal/panel is being opened or closed in response
   *  to a remote-apply tick. Suppresses the local broadcast that would
   *  otherwise echo back through Pusher and cause feedback loops. */
  private suppressActionBroadcast = false;
  private sun: SunSystem | null = null;
  /** Two-way bridge between location.hash and tour state. Foundation for the
   *  AI variant track: generated variants need a stable URL slot to live in. */
  private urlSync: UrlStateSync | null = null;
  private initialUrlState: ParsedUrlState | null = null;

  /**
   * Tint composition. The material panel (designer's emissive choice) and the
   * sun system (time-of-day mood) both push tints into the renderer; we
   * multiply them so the two can coexist without one stomping the other.
   */
  private materialTint: [number, number, number] = [1, 1, 1];
  private sunTint: [number, number, number] = [1, 1, 1];

  /**
   * Current KHR_materials_variants name applied to the active glTF model, if
   * any. Tracked here (not on GltfRenderer) so the material panel's
   * aria-pressed pip is correct, and resets to null on every room change
   * because the GLB's authored default is the new baseline.
   */
  private currentMaterialVariant: string | null = null;

  /** Active tone-swatch id for aria-pressed in the surface panel; "neutral" tint until the user picks one. */
  private currentTintSwatchId: string | null = null;

  /** Guard against re-entrant transitions when a user mashes the UI. */
  private inTransition = false;

  constructor(opts: AppOptions) {
    this.canvas = opts.canvas;
    this.loading = new LoadingScreen();
    this.analytics = getAnalytics();
    this.locale = opts.locale ?? resolveLocale();
    this.t = getT(this.locale);
    // `as unknown as`: the JSON on disk is the WIRE form, where a localisable
    // field may be `{ "pt-BR": …, "en": … }` while RoomsConfig types it as the
    // resolved `string`. TS rightly refuses to see those as comparable, and
    // localizeConfig() on the next line is what makes the claim true.
    this.config = opts.config ?? (roomsData as unknown as RoomsConfig);
    // Resolve the config's bilingual fields down to this locale BEFORE anything
    // reads them — the branding block below is already one such reader. Keeps
    // the binding so a language switch can rewrite the same objects in place
    // rather than rebuilding a config the managers already hold references to.
    this.localizedConfig = localizeConfig(this.config, this.locale);
    this.embed = opts.embed ?? null;
    // Inject previously generated variants from localStorage before any
    // manager sees the config — variants appear in the chip bar on first render.
    //
    // Skipped when the portal supplied the config: those variants were
    // generated against a different project's rooms, and injecting them would
    // put phantom chips into a tour the admin is authoring for a client.
    if (!this.embed) rehydrateGeneratedVariants(this.config.rooms);
    // Apply per-project branding to the loading screen as early as possible —
    // logo + subtitle should show during the first paint, not after the
    // panoramas finish loading. Falls back to the imersivo3D defaults when
    // branding fields are absent.
    this.loading.applyBranding(this.config.branding);
  }

  async start(): Promise<void> {
    // First: publish --topbar-h / --bottombar-h so every fixed-position surface
    // positions off the measured bars rather than the old hardcoded 56/110px.
    // Cheap, DOM-only, and depends on nothing else in this method.
    this.chromeMetrics.install();
    // <html lang> drives screen-reader pronunciation and the TTS voice pick, so
    // it has to agree with the language the chrome is about to render in.
    applyDocumentLocale(this.locale);

    // ── The menu, FIRST ────────────────────────────────────────────────────
    // ORDERING CONTRACT: this must precede `new Ui()`, `new AudioControls()`
    // and `new DebugLook()` further down — all three throw when their element
    // is missing, and since index.html no longer hardcodes the tool buttons,
    // this call is what creates them. Moving it later is a black screen, not a
    // degraded menu.
    this.installMenu();

    this.loading.show();
    this.loading.setStatus('Inicializando renderizador');
    this.loading.setProgress(0.1);

    // ── URL state: parse #room=...&variant=...&yaw=...&pitch=...&time=... before
    // any manager is constructed so the boot path naturally lands on the
    // requested room+variant without emitting a spurious 'api' transition
    // before the synthetic 'init' event from RoomManager.fireInitial().
    this.initialUrlState = parseUrlState();
    applyInitialConfigOverrides(this.config, this.initialUrlState);

    // ── PlayCanvas core
    this.appHandle = createApp(this.canvas, { fov: 72 });
    const { app, camera } = this.appHandle;
    this.controls = new CameraControls(this.canvas, camera, app);
    this.assets = new AssetLoader(app);
    this.fade = new FadeTransition();
    // Renderer strategies (Sprint 2A). One instance of each, App dispatches
    // per room. Default is panorama for any room that doesn't opt-in to
    // gsplat / gltf via rooms.json.
    this.panoRenderer = new PanoramaRenderer(app, this.assets);
    this.gsplatRenderer = new GsplatRenderer(app);
    this.gltfRenderer = new GltfRenderer(app);

    // SunSystem — solar azimuth/elevation feeds the time-of-day slider, the
    // panorama mood tint, and the dollhouse sun-direction arrow. Lat/lon
    // comes from rooms.json branding; defaults to São Paulo. hoursOfDay can
    // come from the URL's #time= so a shared link lands at the same lighting,
    // then from branding.defaultHoursOfDay, then from the viewer's clock.
    // The clock is the wrong default for a demo whose panorama was authored at
    // a fixed hour: it makes the lighting depend on when the buyer clicks.
    const lat = this.config.branding.latitude ?? -23.55;
    const lon = this.config.branding.longitude ?? -46.63;
    const initialHours =
      this.initialUrlState?.hoursOfDay ?? this.config.branding.defaultHoursOfDay ?? undefined;
    this.sun = new SunSystem({
      latitude: lat,
      longitude: lon,
      ...(initialHours !== undefined ? { hoursOfDay: initialHours } : {}),
    });
    this.sun.on((s) => this.handleSunChange(s));

    this.loading.setProgress(0.3);
    this.loading.setStatus('Configurando ambientes');

    // ── State + UI
    this.rooms = new RoomManager(this.config);
    this.variants = new VariantManager();

    // URL ↔ tour-state bridge. Constructed now (deps complete) but not
    // started until after the initial render — that keeps the writer out of
    // the fade-in path and means the first hash write captures the final,
    // post-applyInitialCamera state.
    this.urlSync = new UrlStateSync({
      config: this.config,
      rooms: this.rooms,
      variants: this.variants,
      controls: this.controls,
      sun: this.sun,
    });

    // ?dashboard=1 still auto-opens; H and the menu entry come from the registry.
    this.dashboard = new Dashboard();
    this.modal = new ModalRoot();
    // Co-presence: any modal close — whether the host clicked the × button,
    // hit Escape, or clicked the backdrop — gets broadcast so a guest in
    // 🎯 follow mode sees their copy of the modal dismiss too. Suppressed
    // when the close was itself driven by a remote-apply tick (see
    // applyRemoteAction) so we don't echo ping-pong.
    this.modal.onClose(() => {
      if (this.suppressActionBroadcast) return;
      this.copresence?.broadcastAction({ type: 'hotspot-close' });
    });
    this.materialPanel = new MaterialPanel();
    this.materialPanel.onSelect((swatch) => {
      this.materialTint = [swatch.tint[0], swatch.tint[1], swatch.tint[2]];
      this.currentTintSwatchId = swatch.id;
      this.applyTint();
      this.analytics.log('variant_changed', { kind: 'tint', swatch: swatch.id });
    });
    this.materialPanel.onClose(() => {
      // Leave the tint in place when closing — the buyer chose it on purpose.
      this.controls?.setEnabled(true);
    });
    this.ui = new Ui();
    this.audio = new AudioControls(); // self-wires to #audio-button
    const qr = new QrPanel();
    qr.install(); // self-wires to #qr-button; Q key shortcut
    // Saved-session feature (💾 button + ?s=<code> hydration). Stored as a
    // field so start() can call maybeHydrateFromUrl() after managers exist.
    this.savedSessions = new SavedSessions({
      capture: () => this.captureBuyerSession(),
      apply: (session) => this.applyBuyerSession(session),
      qr,
    });
    this.savedSessions.install();

    // "Variants you explored" gallery (🖼) — durable access to AI-generated
    // variants, which now follow the session cross-device and whose inline
    // chips may be hidden for client presentation (see variantPanel.ts).
    this.variantsGallery = new VariantsGallery({
      modal: this.modal,
      getRooms: () => this.config.rooms,
      applyVariant: (roomId, variantId) => {
        const room = this.rooms?.getById(roomId);
        if (!room || !this.variants) return;
        // Set the variant first so the room change renders it directly
        // (matches applyBuyerSession's order — avoids a double panorama swap).
        if (room.variants.some((v) => v.id === variantId)) {
          this.variants.setVariant(room, variantId, 'api');
        }
        void this.handleRoomSelect(room, 'api');
      },
    });
    this.variantsGallery.install();

    // Co-presence — ghostly cursor for the other participant in a shared
    // tour. Wired here because it needs camera + controls + a way to read
    // the current session code (which the SavedSessions sets after a save
    // or hydration). Button auto-hides when VITE_PUSHER_KEY is unset.
    if (this.controls) {
      const env = (import.meta as ImportMeta & { env?: Record<string, string> }).env ?? {};
      this.copresence = new Copresence({
        app,
        camera,
        controls: this.controls,
        pusherKey: env.VITE_PUSHER_KEY ?? '',
        pusherCluster: env.VITE_PUSHER_CLUSTER ?? 'us2',
        // 🎯 Follow-mode hooks: each broadcast carries the current room +
        // variant; opting in mirrors the whole scene (camera + room +
        // variant), not just the gaze sphere.
        getCurrentRoomVariant: () => {
          const room = this.rooms?.getCurrent();
          if (!room) return null;
          const v = this.variants?.getCurrent(room);
          return { roomId: room.id, variantId: v?.id ?? '' };
        },
        applyRemoteScene: (input) => {
          // Mirror remote variant first so when the room swap fires its
          // post-swap render the new variant is already locked in.
          if (input.roomId) {
            const room = this.rooms?.getById(input.roomId);
            if (room) {
              if (input.variantId) {
                const variantExists = room.variants.some((v) => v.id === input.variantId);
                if (variantExists) this.variants?.setVariant(room, input.variantId, 'api');
              }
              if (this.rooms?.getCurrent()?.id !== room.id) {
                void this.handleRoomSelect(room, 'api');
              }
            }
          }
          // snap=true on the first apply after engaging follow mode so the
          // host's view materializes instantly; subsequent ticks at 10 Hz
          // use the smooth lerp so the guest's camera glides with the host.
          this.controls?.setLook(input.yaw, input.pitch, { snap: input.snap === true });
        },
        applyRemoteAction: (input) => this.applyRemoteAction(input),
      });
      this.copresence.install(() => {
        try {
          return new URL(window.location.href).searchParams.get('s');
        } catch {
          return null;
        }
      });
    }
    this.narrator = new Narrator();
    this.narrator.install(); // self-wires to #narrator-button
    // Voice commands — hands-free navigation in pt-BR. Self-wires to
    // #voice-command-button. Wired AFTER rooms/variants/sun are constructed
    // so callbacks can dispatch immediately.
    // Hidden by default; the operator-only `debug` menu entry (and its D
    // shortcut) is the only way in.
    this.debugLook = new DebugLook(this.controls!);

    const hotspotLayer = document.getElementById('hotspot-layer');
    if (!hotspotLayer) throw new Error('[app] #hotspot-layer missing');
    this.hotspots = new HotspotManager(app, camera, this.canvas, hotspotLayer, (roomId) =>
      this.config.rooms.find((r) => r.id === roomId)?.name,
    );
    this.hotspots.on((e) => this.handleHotspotSelect(e));

    // Sprint 3 Slice 1: optional in-place hotspot editor, gated by ?edit=1.
    // Mounts a toolbar + property panel; clicks on existing hotspots route
    // to the editor instead of their normal modal/cta/etc. handlers. The
    // RoomsConfig is the single source of truth — edits mutate it in place
    // and Export rooms.json downloads the current state.
    this.installEditorMode();

    // Demo Studio: let the portal drive the room list + camera from its own
    // chrome, so the operator doesn't have to hunt for the minimap inside a
    // half-width iframe.
    this.installEmbedControls();

    // Measurement tool — wires its own toolbar toggle. Re-uses the same
    // canvas-tap path: when measure mode is on, taps add a measurement point
    // instead of triggering a hotspot.
    this.measure = new MeasureTool(app, camera, this.canvas, hotspotLayer);
    this.installMeasureButton();

    // Dollhouse 3D plan view in a corner viewport. Click a room box to fade
    // into that room. Visibility persists in localStorage so the buyer's
    // preference survives refreshes.
    const dollhouseButtons = document.getElementById('dollhouse-buttons');
    if (dollhouseButtons) {
      this.dollhouse = new Dollhouse(app, this.canvas, dollhouseButtons);
      this.dollhouse.on((e) => {
        void this.handleRoomSelect(e.room, 'minimap');
      });
      this.installDollhouseToggle();
    }

    // Canvas-tap → pc.Picker. Pixel-perfect pick of the rendered 3D hotspot dot
    // via the engine's picking buffer (filtered to the hotspot layer so empty
    // wall never resolves to a hotspot). Drag is excluded by a small movement
    // threshold so look-around still works. HTML labels remain clickable too as
    // defence in depth.
    this.installCanvasTapPicker();

    // Per-frame: push camera yaw to the floorplan you-are-here arrow AND to
    // the dollhouse pivot so the dollhouse rotates in sync as the user
    // drag-looks (3D compass behaviour). 'update' (not 'postupdate' — PC 2.x
    // doesn't fire that on the app, only on app.systems as 'postUpdate').
    app.on('update', () => {
      if (!this.controls) return;
      const yaw = this.controls.getLook().yaw;
      this.ui?.floorplan.setCameraYaw(yaw);
      this.dollhouse?.setUserYaw(yaw);
      // Sample camera for URL state write — change-detected + debounced
      // inside the sync, so this is cheap when the camera is idle.
      this.urlSync?.tickCamera();
    });

    // 1Hz gaze sampling for the heatmap dashboard. Buckets at 5° resolution
    // happen at the consumer (Dashboard); we just emit the raw yaw/pitch.
    setInterval(() => {
      if (!this.controls || !this.rooms) return;
      const look = this.controls.getLook();
      const room = this.rooms.getCurrent();
      this.analytics.log('gaze_sample', {
        room: room.id,
        yaw: Math.round(look.yaw * 10) / 10,
        pitch: Math.round(look.pitch * 10) / 10,
      });
    }, 1000);

    // ── WebXR
    this.xr = new XrManager(app, camera, this.hotspots);
    new VrButton(this.xr);
    // 3D in-scene info card for VR — HTML overlays are invisible in
    // immersive-vr, so info hotspots used to be effectively dead. The card
    // is constructed once but only shown when XR is active (App.handleHotspotSelect
    // checks xr.isActive() to choose 3D vs. HTML modal).
    this.infoCard3D = new InfoCard3D(app, camera);

    // Idle auto-orbit — attract mode for kiosk demos. After 30s of no user
    // input, slowly rotates the panorama at 3°/s. Any input cancels.
    if (this.controls) {
      // Tilt-to-explore on mobile (button auto-hides on desktop).
      new TiltControls(this.controls).install();
      // IdleOrbit self-registers an app.on('update') listener that keeps it
      // alive for the lifetime of the App — no field assignment needed.
      // eslint-disable-next-line no-new
      new IdleOrbit(app, {
        controls: this.controls,
        isSuppressed: () =>
          Boolean(this.xr?.isActive()) ||
          Boolean(this.editorMode?.isActive()) ||
          Boolean(this.measure?.isActive()),
      });
    }
    // Cloud STT for VR voice navigation. The Quest Browser has no Web Speech
    // API, so the 🗣 button (VoiceCommands) is dead in the headset — grip-to-
    // talk records mic audio → Groq Whisper → the same command dispatcher.
    this.cloudVoice = CloudVoiceInput.isSupported() ? new CloudVoiceInput() : null;

    this.xr.on((e) => {
      if (e.type === 'enter') {
        // While in VR, the HTML overlays are invisible — disable look-around (XR drives
        // the camera directly) and dismiss any open modal.
        this.controls?.setEnabled(false);
        this.modal?.close();
        document.getElementById('hotspot-layer')?.classList.add('hidden');
        // The dollhouse + floor plan are distracting floating panels in VR — hide
        // them for the duration of the immersive session (restored on exit).
        this.dollhouse?.setVisible(false);
        this.ui?.floorplan.hide();
        // Warm the mic so the first grip-to-talk has no permission/setup delay.
        // Best-effort; the granted permission persists across Quest sessions.
        void this.cloudVoice?.prewarm();
        // Prime the WebAudio cue context while we (likely) still have user
        // activation from the "Entrar VR" tap.
        this.ensureCueCtx();
        this.announceVrVoice();
      } else if (e.type === 'exit') {
        this.controls?.setEnabled(true);
        document.getElementById('hotspot-layer')?.classList.remove('hidden');
        // Hide any in-scene info card when leaving VR — the HTML modal flow
        // takes over for non-immersive interaction.
        this.infoCard3D?.hide();
        this.cloudVoice?.cancel();
        // Restore the dollhouse + floor plan to whatever the operator's prefs say.
        this.restoreOverlaysFromPrefs();
      } else if (e.type === 'squeeze-start') {
        this.startVrVoiceCommand();
      } else if (e.type === 'squeeze-end') {
        void this.finishVrVoiceCommand();
      }
    });

    this.ui.setHandlers({
      onVariantSelect: (variant) => this.handleVariantSelect(variant),
      onRoomSelect: (room) => this.handleRoomSelect(room, 'minimap'),
      onBeforeAfterToggle: () => this.handleBeforeAfter(),
    });
    this.ui.initialize(this.config);
    this.dollhouse?.setRooms(this.config.rooms, this.config.defaultRoomId, this.config.features ?? []);
    this.installFloorplanToggle();

    // Sun panel — wires after dollhouse so the initial sun state can fan out
    // to both the renderer tint and the dollhouse arrow in one shot.
    new SunPanel(this.sun);

    // Voice commands — wired after rooms / variants / sun / handlers exist
    // so the callbacks can dispatch immediately. Self-wires to
    // #voice-command-button; hidden in browsers without Web Speech API.
    this.voiceCommands = new VoiceCommands({
      config: this.config,
      navigateToRoom: (roomId) => {
        const room = this.rooms?.getById(roomId);
        if (room) void this.handleRoomSelect(room, 'api');
      },
      setVariant: (variantId) => {
        if (!this.rooms || !this.variants) return;
        const room = this.rooms.getCurrent();
        this.variants.setVariant(room, variantId, 'api');
      },
      toggleBeforeAfter: () => this.handleBeforeAfter(),
      toggleNarrator: () => this.narrator?.setEnabled(!this.narrator.isEnabled()),
      showQr: () => {
        const btn = document.getElementById('qr-button') as HTMLButtonElement | null;
        btn?.click();
      },
      setSunHour: (hour) => this.sun?.setTimeOfDay(hour),
      enterApartment: () => this.enterApartment(),
      selectScene: (role) => this.selectScene(role),
      currentHotspots: () => this.rooms?.getCurrent().hotspots ?? [],
      openInfo: (hotspot) => this.openInfoHotspot(hotspot),
      activateMenu: (item) => activateMenuItem(item, this.menuContext()),
      menuIndex: () => this.menuVoiceIndex,
    });
    this.voiceCommands.install();

    // Variant comparison drag slider — live dual-camera 360 reveal for
    // panorama rooms (drag-look stays live on both sides), frozen-snapshot
    // fallback for gsplat/glTF rooms. See docs/BEFORE-AFTER-SLIDER-WORKPLAN.md.
    const variantCompare = new VariantCompare({
      app,
      canvas: this.canvas,
      camera,
      assets: this.assets,
      swapAndRender: async (room, variant) => {
        await this.swapPanoramaImmediate(room, variant);
        // One render pass ensures the new texture is on the canvas before
        // toDataURL() captures it.
        app.render();
      },
      onOpen: () => {
        // Same "other overlays are noise here" treatment as entering VR.
        document.getElementById('hotspot-layer')?.classList.add('hidden');
        this.dollhouse?.setVisible(false);
        this.ui?.floorplan.hide();
      },
      onClose: () => {
        document.getElementById('hotspot-layer')?.classList.remove('hidden');
        this.restoreOverlaysFromPrefs();
      },
    });
    document.getElementById('compare-button')?.addEventListener('click', () => {
      if (!this.rooms || !this.variants) return;
      const room = this.rooms.getCurrent();
      const current = this.variants.getCurrent(room);
      const beforeId = room.beforeVariantId;
      const compareWith = beforeId ? room.variants.find((v) => v.id === beforeId) : null;
      if (!compareWith) {
        showToast('Esta sala não tem variante "Antes" configurada', { tone: 'error' });
        return;
      }
      void variantCompare.open(room, current, compareWith);
    });

    // Cinematic auto-tour — pairs with the narrator for a self-running demo.
    if (this.controls) {
      new AutoTour({
        config: this.config,
        controls: this.controls,
        getCurrentRoom: () => this.rooms?.getCurrent() ?? null,
        navigateToRoom: async (roomId) => {
          const room = this.rooms?.getById(roomId);
          if (room) await this.handleRoomSelect(room, 'api');
        },
        isSuppressed: () =>
          Boolean(this.xr?.isActive()) ||
          Boolean(this.editorMode?.isActive()) ||
          Boolean(this.measure?.isActive()),
      }).install();
    }
    this.handleSunChange(this.sun.getState());

    // Share button — copies a /r/<roomId>?... URL to the clipboard. Uses the
    // pre-baked per-room landing pages built by scripts/generate-og.mjs so
    // link unfurlers (Slack / WhatsApp / iMessage) see the right OG preview.
    this.installShareButton();
    this.installWhatsAppButton();
    // Initialize notes BEFORE installNoteButton so the toggle handler has
    // an instance to write into. Notes auto-load any localStorage draft.
    this.notes = new NotesManager();
    this.installNoteButton();
    // In-tour LLM chat assistant — Claude Sonnet 4.6 answers buyer
    // questions ("qual o pé direito da sala?") using a rooms.json
    // projection as ground-truth context. Reuses ANTHROPIC_API_KEY.
    this.chatPanel = new ChatPanel({
      getContext: () => this.buildChatContext(),
    });
    this.chatPanel.install();
    this.installReportButton();

    // Surface ?splat= / ?gltf= URL overrides visibly so operators know they're
    // active (the smoke-test path; see docs/CAPTURE-PIPELINE.md). Without
    // this a silently-failing asset load looks identical to "nothing happened".
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const splatRaw = params.get('splat');
      if (splatRaw) showToast(`Gsplat override ativo: ${splatRaw}`, { tone: 'info' });
      const gltfRaw = params.get('gltf');
      if (gltfRaw) showToast(`GLTF override ativo: ${gltfRaw}`, { tone: 'info' });
    }

    // Disable look-around while a modal is open so the camera doesn't drift behind the dialog.
    this.modal.onClose(() => this.controls?.setEnabled(true));

    // Wire managers to render side effects.
    this.rooms.on((e) => {
      if (!this.variants || !this.ui || !this.hotspots) return;
      this.ui.setRoom(e.current, this.variants.getCurrent(e.current).id);
      this.applyHotspotsForRoom(e.current);
      // Each room can carry its own metres-per-radian calibration for the
      // measurement tool. Apply it on every room change; clear in-progress
      // measurements so the operator doesn't compare across rooms.
      this.measure?.clear();
      this.measure?.setMetresPerRadian(e.current.scale?.metresPerRadian ?? 2.0);
      this.dollhouse?.setCurrentRoom(e.current.id);
      // Drop any per-room material tint and close the material panel so the
      // next room renders with neutral colour. Sun tint persists across rooms
      // because the time-of-day is a global apartment-wide setting. The glTF
      // material-variant pick stays sticky — GltfRenderer caches the loaded
      // model across enable/disable cycles, so resetting App-side without
      // also reverting the model would leave the panel out of sync.
      this.materialTint = [1, 1, 1];
      this.currentTintSwatchId = null;
      this.applyTint();
      this.materialPanel?.close();
      // Drop the Phase 3 material-overlay state too — the new room's surface
      // hotspots will own a different mask + finish set. release() frees the
      // cached mask texture so GPU memory stays flat over a long tour.
      this.currentMaterialFinishId = null;
      this.materialOverlay?.release();
      // Edit-mode compass slider should track the new room's yawOffsetDeg.
      this.editorMode?.syncCompassFromRoom();
      // Per-room ambient audio swap. Crossfades only if audio is currently
      // enabled; otherwise queues the URL for the next toggle. Null means
      // fall back to the project default ambient track.
      this.audio?.setRoomAmbient(e.current.ambientAudio ?? null);
      // Speak the room narration when the tour-narrated toggle is on.
      // Skip the synthetic 'init' event — autoplay rules block speech
      // before a user gesture.
      if (e.trigger !== 'init') this.narrator?.speak(e.current.narration);
      this.variants.emitForRoom(e.current, e.trigger === 'init' ? 'init' : 'roomchange');
      this.analytics.log('room_viewed', { room: e.current.id, trigger: e.trigger });
      if (e.trigger !== 'init') this.scheduleAutosave();
    });

    this.variants.on((e) => {
      if (e.trigger === 'init' || e.trigger === 'roomchange') {
        // Room change handles the fade; just swap texture inside it.
        this.swapPanorama(e.room, e.currentVariant, { fade: false });
      } else {
        this.swapPanorama(e.room, e.currentVariant, { fade: true });
        if (e.trigger === 'beforeafter') {
          this.analytics.log('beforeafter_toggled', { room: e.room.id, showingBefore: e.currentVariant.isBefore === true });
        } else {
          this.analytics.log('variant_changed', { room: e.room.id, variant: e.currentVariant.id, trigger: e.trigger });
        }
      }
      if (this.ui) this.ui.setActiveVariant(e.currentVariant.id, e.currentVariant.isBefore === true);
      // Edit-mode alignment slider tracks the variant now on screen, the same
      // way the compass tracks the room.
      this.editorMode?.syncPanoramaYawFromVariant();
      // Dollhouse mood-tint mirror: each room's box wears its current variant's
      // colour, so the buyer can scan the apartment at a glance without entering
      // every room.
      this.dollhouse?.setRoomVariant(e.room.id, e.currentVariant.id);
      if (e.trigger !== 'init' && e.trigger !== 'roomchange') this.scheduleAutosave();
    });

    this.loading.setProgress(0.55);
    this.loading.setStatus('Carregando ambiente inicial');

    // Initial render under a fade — we open already black, fire the room init event,
    // wait for the panorama to load, then fade in.
    await this.fade.out(0);
    this.rooms.fireInitial();

    const firstRoom = this.rooms.getCurrent();
    const firstVariant = this.variants.getCurrent(firstRoom);
    await this.swapPanoramaImmediate(firstRoom, firstVariant);
    this.applyInitialCamera(firstRoom);

    // URL → camera/sun overrides — applied AFTER applyInitialCamera so they
    // beat the room's authored initialYaw/Pitch when a deep link is present.
    // Sun was already seeded at construction time via the URL's #time= if
    // any; this is just the camera half.
    if (this.initialUrlState) {
      this.urlSync?.applyInitialCameraAndSun(this.initialUrlState);
    }
    // Now safe to start the two-way sync — writer captures the final
    // post-override state, and subsequent manager events / camera ticks
    // keep the hash live.
    this.urlSync?.start();

    // Buyer-session hydration (?s=<code>) — happens AFTER the initial room
    // is rendered, so the apply() pivot is a smooth single-room transition
    // rather than a cold-paint shuffle.
    if (this.savedSessions) await this.savedSessions.maybeHydrateFromUrl();

    this.loading.setProgress(1);
    this.loading.setStatus('Pronto');
    await waitOneFrame();
    this.loading.hide();
    await this.fade.in();
  }

  /**
   * Read by scripts/verify-responsive-chrome.mjs through window.__imersivo, so
   * the reachability assertion can compare what the registry declares against
   * what is actually on screen — rather than hardcoding a button list that
   * would rot the first time an entry moves tier.
   */
  debugMenu(): { id: string; tier: string; audience: string; inRail: boolean }[] {
    return this.menu?.debugItems() ?? [];
  }

  destroy(): void {
    this.menu?.destroy();
    this.chromeMetrics.destroy();
    this.urlSync?.destroy();
    this.cloudVoice?.release();
    this.xr?.destroy();
    this.measure?.destroy();
    this.dollhouse?.destroy();
    this.hotspots?.destroy();
    this.controls?.destroy();
    this.panoRenderer?.destroy();
    this.gsplatRenderer?.destroy();
    this.gltfRenderer?.destroy();
    this.materialOverlay?.destroy();
    this.assets?.destroy();
    this.appHandle?.detach();
  }

  // ── UI handlers ───────────────────────────────────────────────

  // ── Saved buyer sessions ──────────────────────────────────────

  /** Gathers the current state into a BuyerSession (sans server-stamped fields). */
  private captureBuyerSession(): Omit<BuyerSession, 'sessionCode' | 'savedAt'> {
    const room = this.rooms?.getCurrent();
    const look = this.controls?.getLook() ?? { yaw: 0, pitch: 0, fov: 72 };
    const variants: Record<string, string> = {};
    if (this.rooms && this.variants) {
      for (const r of this.config.rooms) {
        const v = this.variants.getCurrent(r);
        if (v) variants[r.id] = v.id;
      }
    }
    const audioBtn = document.getElementById('audio-button');
    const audioOn = audioBtn?.querySelector('.icon-button__icon')?.getAttribute('data-state') === 'on';
    return {
      // Carry this device's generated-variant project id so the session's
      // AI variants follow a `?s=` share link to another device.
      projectId: getProjectId(),
      lastRoom: room?.id ?? this.config.rooms[0]?.id ?? '',
      variants,
      yaw: look.yaw,
      pitch: look.pitch,
      hoursOfDay: this.sun?.getState().hoursOfDay ?? 12,
      audioOn,
      narratorOn: this.narrator?.isEnabled() ?? false,
      notes: this.notes?.all() ?? [],
    };
  }

  /** Applies a previously-saved BuyerSession. Best-effort: skips fields that
   *  reference rooms/variants that no longer exist (the project might have
   *  evolved since the buyer last saved). */
  private applyBuyerSession(session: BuyerSession): void {
    if (!this.rooms || !this.variants) return;
    this.hydratingSession = true;
    // Restore per-room variant choices first so variant.emitForRoom uses
    // the right one when the active room is set.
    for (const [roomId, variantId] of Object.entries(session.variants)) {
      const room = this.rooms.getById(roomId);
      if (!room) continue;
      const variantExists = room.variants.some((v) => v.id === variantId);
      if (variantExists) this.variants.setVariant(room, variantId, 'api');
    }
    // Navigate to the last room.
    const lastRoom = session.lastRoom ? this.rooms.getById(session.lastRoom) : null;
    if (lastRoom) void this.handleRoomSelect(lastRoom, 'api');
    // Restore camera look.
    this.controls?.setLook(session.yaw, session.pitch, { snap: true });
    // Restore sun time of day.
    if (typeof session.hoursOfDay === 'number') this.sun?.setTimeOfDay(session.hoursOfDay);
    // Restore narrator toggle (audio toggle requires a user gesture for
    // autoplay, so we don't programmatically enable it).
    if (session.narratorOn && this.narrator && !this.narrator.isEnabled()) {
      this.narrator.setEnabled(true);
    }
    // Restore buyer-authored sticky notes — replaces any localStorage
    // draft from a prior browser. Scope the notes shadow to the session
    // code so a device that loads multiple `?s=` URLs doesn't blend them.
    if (session.notes && this.notes) {
      this.notes.replaceAll(session.notes);
      this.notes.setStorageScope(session.sessionCode);
      // Re-render hotspots for the now-active room so the notes appear.
      const active = this.rooms.getCurrent();
      this.applyHotspotsForRoom(active);
    }
    // Chat history isn't transported in BuyerSession (privacy: conversations
    // stay on the buyer's device). But we DO re-scope the localStorage key
    // to the session code so multiple buyers loading different `?s=` URLs
    // on the same device don't see each other's chat history.
    this.chatPanel?.setStorageScope(session.sessionCode);

    // Re-key this device to the buyer's project and pull their AI-generated
    // variants from Vercel Blob, so a session opened on another device shows
    // the variants they generated — not just the rooms.json defaults. Async
    // (network) so the fast cold-paint restore above isn't blocked.
    const projectId = session.projectId;
    if (projectId && /^[0-9a-f-]{36}$/.test(projectId)) {
      setProjectId(projectId);
      void importFromServer(projectId, this.config.rooms)
        .then((n) => {
          if (n <= 0) return;
          const cur = this.rooms?.getCurrent();
          if (cur && this.variants) {
            // The buyer's last-viewed variant may have been one of the
            // just-imported ones — apply it now that the chip exists.
            const savedVid = session.variants[cur.id];
            if (savedVid && cur.variants.some((v) => v.id === savedVid)) {
              this.variants.setVariant(cur, savedVid, 'api');
            }
            this.ui?.setRoom(cur, this.variants.getCurrent(cur).id);
          }
          showToast(`${n} variante(s) gerada(s) recuperada(s) da sua sessão`, { tone: 'info' });
        })
        .catch((err: unknown) => console.warn('[session] generated-variant import failed', err))
        .finally(() => {
          this.hydratingSession = false;
        });
    } else {
      this.hydratingSession = false;
    }
  }

  /**
   * Debounced background autosave. Persists meaningful state changes into the
   * active session (if one exists) so a tour stays current without the buyer
   * tapping 💾 again. No-op until the first manual save (autosaveIfActive only
   * writes when a session code is set), and suppressed during hydration.
   */
  private scheduleAutosave(): void {
    if (this.hydratingSession) return;
    if (this.autosaveTimer) clearTimeout(this.autosaveTimer);
    this.autosaveTimer = setTimeout(() => {
      this.autosaveTimer = null;
      void this.savedSessions?.autosaveIfActive();
    }, 4000);
  }

  private handleVariantSelect(variant: Variant): void {
    if (!this.rooms || !this.variants) return;
    const room = this.rooms.getCurrent();
    this.variants.setVariant(room, variant.id, 'click');
  }

  /**
   * "Entrar no apartamento" voice command — step from the current room into
   * the unit. Follows the current room's first navigation hotspot (e.g. the
   * building hall's door → `entrada`); falls back to an `entrada` room if one
   * exists, else the default room. Demo-agnostic.
   */
  private enterApartment(): void {
    if (!this.rooms) return;
    const current = this.rooms.getCurrent();
    const nav = current.hotspots.find((h): h is Extract<Hotspot, { type: 'navigation' }> => h.type === 'navigation');
    const target =
      (nav ? this.rooms.getById(nav.targetRoomId) : null) ??
      this.rooms.getById('entrada') ??
      this.rooms.getById(this.config.defaultRoomId);
    if (target) void this.handleRoomSelect(target, 'api');
  }

  /**
   * Tell the visitor, on entering VR, that voice is the control surface.
   *
   * This is not polish. In a headset the entire menu is gone — no rail, no
   * sheet, no icons, because none of that HTML is composited into an immersive
   * session and `MenuItem.vr` was never implemented. Grip-to-talk has always
   * been armed here, but nothing on screen said so, which made an available
   * feature indistinguishable from an absent one.
   *
   * Named after the room so the first card also confirms the tour loaded, and
   * localised, because the card is the one piece of chrome a headset does see.
   */
  private announceVrVoice(): void {
    if (!this.cloudVoice) return;
    const room = this.rooms?.getCurrent();
    const example =
      this.locale === 'en'
        ? 'Hold the grip and say: "go to the kitchen", "turn on sound", "tell me about the sofa".'
        : 'Segure o grip e diga: "ir para a cozinha", "ligar o som", "me fale sobre o sofá".';
    this.showVrCard(
      this.locale === 'en' ? '🎤 Voice is on' : '🎤 Comando de voz ativo',
      room ? `${room.name} · ${example}` : example,
    );
    this.speakVr(
      this.locale === 'en'
        ? 'Voice control is on. Hold the grip button and say a command.'
        : 'Comando de voz ativo. Segure o gatilho lateral e diga um comando.',
    );
  }

  /**
   * Begin a VR push-to-talk utterance (controller grip down). Gives immediate
   * VISIBLE feedback (red reticle + 3D card) so the operator knows the grip
   * registered — the only reliable channel in immersive-vr. Surfaces a mic
   * failure instead of silently doing nothing. The start promise is stored so
   * finishVrVoiceCommand can await it (avoids a quick-tap start/stop race).
   */
  private startVrVoiceCommand(): void {
    if (!this.cloudVoice) return;
    console.log('[vrvoice] squeeze-start → arming recording');
    this.playCue('start'); // rising beep = "listening" (reliable in VR)
    this.xr?.setVoiceIndicator(true);
    this.showVrCard('🎤 Ouvindo…', 'Fale o comando e solte o gatilho lateral (grip).');
    this.vrVoiceStart = this.cloudVoice.start();
    void this.vrVoiceStart.then((ok) => {
      if (!ok) {
        console.warn('[vrvoice] recording failed to start (mic blocked in VR?)');
        this.xr?.setVoiceIndicator(false);
        this.showVrCard('Microfone indisponível', 'Permita o microfone para este site (Configurações do Quest → Permissões) e tente de novo.');
      }
    });
  }

  /**
   * Finish a VR push-to-talk utterance (controller grip up): stop recording,
   * transcribe via Groq Whisper, and run the result through the same command
   * dispatcher as the 🗣 button. Feedback is shown on the 3D info card (visible
   * in VR) and spoken; every failure path reports rather than no-op'ing.
   */
  private async finishVrVoiceCommand(): Promise<void> {
    if (!this.cloudVoice) return;
    console.log('[vrvoice] squeeze-end');
    this.playCue('stop'); // falling beep = "stopped listening"
    this.xr?.setVoiceIndicator(false);
    // Wait for a just-issued start() to settle so a quick grip tap still records.
    if (this.vrVoiceStart) {
      try {
        await this.vrVoiceStart;
      } catch {
        /* ignore */
      }
      this.vrVoiceStart = null;
    }
    if (!this.cloudVoice.isRecording()) {
      console.warn('[vrvoice] no active recording at release');
      return; // startVrVoiceCommand already showed the mic-failure card
    }
    this.showVrCard('Transcrevendo…', 'Um instante.');
    const text = await this.cloudVoice.stop();
    console.log('[vrvoice] transcript:', JSON.stringify(text));
    if (!text) {
      this.showVrCard('Não captei áudio', 'Segure o grip, fale o comando e solte.');
      this.speakVr('Não captei.');
      return;
    }
    const handled = this.voiceCommands?.dispatch(text) ?? false;
    console.log('[vrvoice] dispatched:', handled, 'text:', text);
    if (handled) {
      this.showVrCard(`✓ ${text}`, '');
    } else {
      this.showVrCard('Não entendi', `"${text}" — ex.: "ir para o banheiro", "entrar no apartamento".`);
      this.speakVr('Não entendi.');
    }
  }

  /**
   * Open an info hotspot through the same fork the click path uses: the 3D
   * card in immersive-vr (HTML modals are not composited there), the modal
   * everywhere else.
   *
   * Exists because in VR there is no other way in. The floating HTML labels
   * that a phone or desktop visitor taps are hidden on XR enter, so an info
   * hotspot is unreachable in a headset except by naming it out loud.
   */
  private openInfoHotspot(hotspot: Extract<Hotspot, { type: 'info' }>): void {
    if (this.xr?.isActive() && this.infoCard3D) {
      this.infoCard3D.show(hotspot);
    } else {
      this.modal?.close();
      openInfoModal(this.modal!, hotspot);
    }
    this.analytics.log('hotspot_clicked', {
      hotspot: hotspot.id,
      label: hotspot.label,
      type: hotspot.type,
      // `source` distinguishes this from a tap in the dashboard, and it is the
      // only way to see whether anyone in a headset actually uses voice.
      source: 'voice',
    });
  }

  /** Show a transient message on the 3D info card — the VR-visible feedback
   *  surface (only label + description are read, so a synthetic hotspot is fine). */
  private showVrCard(label: string, body: string): void {
    this.infoCard3D?.show({ type: 'info', id: 'vrvoice', label, description: body, yaw: 0, pitch: 0 } as unknown as InfoHotspot);
  }

  /**
   * Switch the current room to a specific scene by role. "antes" → the before
   * variant (obra), "depois" → the move-in/default variant (pronto/limpo),
   * "projeto" → a render/projeto variant if the room has one. Returns whether
   * a matching variant was found + applied.
   */
  private selectScene(role: 'antes' | 'depois' | 'projeto'): boolean {
    if (!this.rooms || !this.variants) return false;
    const room = this.rooms.getCurrent();
    const isProjeto = (v: Variant): boolean => /projeto|render/i.test(v.id) || /projeto|render/i.test(v.name);
    let variantId: string | undefined;
    if (role === 'antes') {
      variantId = room.beforeVariantId ?? room.variants.find((v) => v.isBefore)?.id;
    } else if (role === 'depois') {
      variantId =
        room.defaultVariant ?? room.variants.find((v) => !v.isBefore && !isProjeto(v))?.id;
    } else {
      variantId = room.variants.find(isProjeto)?.id;
    }
    if (!variantId) return false;
    this.variants.setVariant(room, variantId, 'api');
    return true;
  }

  /**
   * Whether the dollhouse should be ON for someone who has never touched the
   * toggle.
   *
   * The dollhouse draws one box per room from `floorplan.shape`. A config with
   * no floorplan geometry on any room therefore renders an EMPTY corner
   * viewport — a grey rectangle sitting over the panorama with nothing in it.
   * Defaulting it on in that case is just showing the buyer a broken widget,
   * which is what pipeline-generated demos did (Phase 2 geometry extraction is
   * unbuilt, so `generate-rooms-draft` deliberately omits `floorplan` rather
   * than inventing polygons).
   *
   * So the default follows the data: on when there is something to draw, off
   * when there isn't. An explicit user preference in localStorage always wins
   * over this — the button still works either way, and a demo that later gains
   * geometry starts defaulting to on with no code change.
   */
  private dollhouseDefaultVisible(): boolean {
    // An explicit config opt-out wins — used by demos whose plan is estimated
    // rather than measured, which should still open from the 🏠 button but
    // shouldn't greet the buyer.
    if (typeof this.config.dollhouseVisibleByDefault === 'boolean') {
      return this.config.dollhouseVisibleByDefault;
    }
    // Never greet a phone with it. The dollhouse is a second viewport painted
    // over the panorama, so on a small screen it covers the thing being sold —
    // the same reason the room panel starts closed there (floorplanDefaultVisible).
    // It stays one tap away on the 🏠 menu entry, and an explicit preference in
    // localStorage still wins, so this only changes the FIRST visit.
    if (isCoarsePointer()) return false;
    return this.config.rooms.some((r) => (r.floorplan?.shape?.length ?? 0) > 0);
  }

  /**
   * The room panel opens by default on a pointer device and stays closed on a
   * touch one.
   *
   * On a desktop it sits in the margin. On a phone it covers the panorama —
   * reported on Lambert Fine Arts, where 11 rooms filled most of the screen the
   * visitor came to look at. The panel is one tap away either way, so the cost
   * of defaulting closed is a tap and the cost of defaulting open is the view.
   *
   * An explicit preference in localStorage always wins, on both.
   */
  private floorplanDefaultVisible(): boolean {
    return !isCoarsePointer();
  }

  /** Read a visibility pref, falling back to the supplied default. */
  private prefVisible(key: string, fallback: boolean): boolean {
    const stored = localStorage.getItem(key);
    if (stored === null) return fallback;
    return stored !== '0';
  }

  /** Re-apply the operator's dollhouse + floor-plan visibility prefs (used on
   *  VR exit, where both were force-hidden for the immersive session). */
  private restoreOverlaysFromPrefs(): void {
    const floorVisible = this.prefVisible('imersivo:floorplanVisible', this.floorplanDefaultVisible());
    if (floorVisible) this.ui?.floorplan.show();
    else this.ui?.floorplan.hide();
    const dollVisible = this.prefVisible('imersivo:dollhouseVisible', this.dollhouseDefaultVisible());
    this.dollhouse?.setVisible(dollVisible);
  }

  /** Lazily create + resume the WebAudio context used for VR push-to-talk cues. */
  private ensureCueCtx(): AudioContext | null {
    try {
      if (!this.cueCtx) {
        const Ctor =
          window.AudioContext ??
          (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
        if (!Ctor) return null;
        this.cueCtx = new Ctor();
      }
      if (this.cueCtx.state === 'suspended') void this.cueCtx.resume();
      return this.cueCtx;
    } catch {
      return null;
    }
  }

  /**
   * Play a short WebAudio cue. 'start' = rising beep (headset is listening),
   * 'stop' = falling beep (stopped listening). Audio is the reliable feedback
   * channel in immersive-vr where DOM overlays don't render.
   */
  private playCue(kind: 'start' | 'stop'): void {
    const ctx = this.ensureCueCtx();
    if (!ctx) return;
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.connect(gain);
    gain.connect(ctx.destination);
    if (kind === 'start') {
      osc.frequency.setValueAtTime(660, now);
      osc.frequency.exponentialRampToValueAtTime(990, now + 0.12);
    } else {
      osc.frequency.setValueAtTime(720, now);
      osc.frequency.exponentialRampToValueAtTime(420, now + 0.14);
    }
    const dur = kind === 'start' ? 0.15 : 0.17;
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.25, now + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + dur);
    osc.start(now);
    osc.stop(now + dur + 0.02);
  }

  /** Short spoken feedback for VR. speechSynthesis can be unreliable in an
   *  immersive session on Quest, so this is a best-effort complement to the
   *  3D info card — never the sole feedback channel. */
  private speakVr(text: string): void {
    try {
      const synth = window.speechSynthesis;
      if (!synth) return;
      synth.cancel();
      const utt = new SpeechSynthesisUtterance(text);
      utt.lang = 'pt-BR';
      synth.speak(utt);
    } catch {
      /* speechSynthesis unavailable — silent */
    }
  }

  private async handleRoomSelect(room: Room, trigger: 'minimap' | 'navigation' | 'api' = 'minimap'): Promise<void> {
    if (!this.rooms || this.inTransition) return;
    if (room.id === this.rooms.getCurrent().id) return;
    this.inTransition = true;
    this.modal?.close();
    try {
      if (!this.fade) return;
      await this.fade.cycle(async () => {
        this.rooms!.setRoom(room.id, trigger);
        const newRoom = this.rooms!.getCurrent();
        const variant = this.variants!.getCurrent(newRoom);
        await this.swapPanoramaImmediate(newRoom, variant);
        this.applyInitialCamera(newRoom);
      });
    } finally {
      this.inTransition = false;
    }
  }

  private handleBeforeAfter(): void {
    if (!this.rooms || !this.variants) return;
    const room = this.rooms.getCurrent();
    this.variants.toggleBeforeAfter(room);
  }

  private installEditorMode(): void {
    const mode = new EditorMode({
      config: this.config,
      getCameraLook: () => {
        const look = this.controls?.getLook();
        return { yaw: look?.yaw ?? 0, pitch: look?.pitch ?? 0 };
      },
      getActiveRoom: () => this.rooms?.getCurrent() ?? null,
      getActiveVariant: () => {
        if (!this.rooms || !this.variants) return null;
        const room = this.rooms.getCurrent();
        const variant = this.variants.getCurrent(room);
        if (!variant) return null;
        return {
          variantId: variant.id,
          panoramaUrl: variant.panoramaUrl,
          name: variant.name,
          generatedStyle: variant.generatedStyle,
          generatedPrompt: variant.generatedPrompt,
        };
      },
      embedded: this.embed !== null,
      onSaveRequested: () => {
        // Queue unconditionally before flushing: with nothing pending (the
        // debounce already fired) a bare flush sends nothing, and the operator
        // would get a "saved" toast for a message that never left.
        this.embed?.notifyConfigChanged(this.config, 'manual');
        this.embed?.flush();
      },
      onHotspotAdded: (hotspot, room) => {
        // Re-render the layer so the new dot + label appear, then open the
        // property panel so the operator can immediately label it.
        this.applyHotspotsForRoom(room);
        this.editorPanel?.openFor(hotspot);
        this.notifyTourConfigChanged('hotspot-added');
      },
      getActiveVariantObject: () => {
        const room = this.rooms?.getCurrent() ?? null;
        if (!room) return null;
        const variant = this.variants?.getCurrent(room) ?? null;
        return variant ? { room, variant } : null;
      },
      onVariantYawOffsetChange: (_room, variant, yawOffsetDeg) => {
        // Mutate in place so the value travels with the config like every other
        // edit-mode change, then push it straight into the live renderer. The
        // renderer call is what makes the drag preview: re-running swapPanorama
        // per input event would reload the texture on every pixel of travel.
        variant.yawOffsetDeg = yawOffsetDeg;
        this.panoRenderer?.setYawOffset(yawOffsetDeg);
        this.notifyTourConfigChanged('panorama-yaw');
      },
      onRoomYawOffsetChange: (room, yawOffsetDeg) => {
        // Mutate in place + re-render the floorplan + dollhouse so the
        // operator sees the rotation immediately. Persistence is on Export
        // like every other edit-mode mutation.
        if (room.floorplan) room.floorplan.yawOffsetDeg = yawOffsetDeg;
        this.ui?.floorplan.setCurrentRoom(room.id);
        this.dollhouse?.setCurrentRoom(room.id);
        this.notifyTourConfigChanged('compass');
      },
    });
    if (!mode.isActive()) return;
    // Only embed mode gets an image picker: it is the portal that has the
    // session, the bucket and the project_assets row. Standalone ?edit=1 keeps
    // the plain URL field — that operator has a demos/ folder instead.
    const bridge = this.embed;
    const panel = new EditorPanel(
      () => this.config.rooms,
      bridge
        ? (hotspot) =>
            bridge
              .requestAsset({
                kind: 'info-image',
                roomId: this.rooms?.getCurrent()?.id ?? '',
                hotspotId: hotspot.id,
                currentAssetPath:
                  (hotspot as unknown as Record<string, unknown>)['imageAssetPath'] as
                    | string
                    | undefined,
              })
              .then((picked) => ({ assetPath: picked.assetPath, url: picked.url }))
        : null,
    );
    panel.onChange(() => {
      // The hotspot object was mutated in place by the panel; just trigger
      // a re-spawn so labels and dot positions update.
      const room = this.rooms?.getCurrent();
      if (room) this.applyHotspotsForRoom(room);
      // The count in the toolbar is the only signal once the panel is closed,
      // so it has to move the moment a destination is chosen or cleared.
      this.editorMode?.refreshNavProblems();
      this.notifyTourConfigChanged('hotspot-changed');
    });
    panel.onDelete((hotspot) => {
      const room = this.rooms?.getCurrent();
      if (!room) return;
      const idx = room.hotspots.findIndex((h) => h.id === hotspot.id);
      if (idx >= 0) room.hotspots.splice(idx, 1);
      this.applyHotspotsForRoom(room);
      this.editorMode?.refreshNavProblems();
      this.notifyTourConfigChanged('hotspot-deleted');
    });
    mode.attach();
    // The toolbar exists only now, so it has to be tracked after the fact.
    // Everything that must stay clear of it (the minimap's room list, the menu
    // sheet) positions off --editor-toolbar-h; it stays 0px outside edit mode,
    // which makes those rules collapse to the plain no-toolbar case.
    this.chromeMetrics.trackLate('editor-toolbar', '--editor-toolbar-h', 0);
    this.editorMode = mode;
    this.editorPanel = panel;
  }

  /**
   * Report the whole live RoomsConfig to the portal. No-op outside ?embed=1.
   *
   * The whole config goes over rather than a patch: the editor mutates the
   * object graph in place from a dozen call sites, so there is no single place
   * that knows what changed — and a patch protocol would need one. The bridge
   * debounces, so a drag gesture still ends in one message.
   */
  private notifyTourConfigChanged(reason: TourConfigChangeReason): void {
    this.embed?.notifyConfigChanged(this.config, reason);
  }

  /** Parent→iframe controls for the Demo Studio editor. No-op when not embedded. */
  private installEmbedControls(): void {
    const embed = this.embed;
    if (!embed) return;
    embed.onRoomSelect((roomId) => {
      const room = this.rooms?.getById(roomId);
      if (!room) return;
      void this.handleRoomSelect(room, 'api');
    });
    embed.onLookSet((yaw, pitch) => {
      // Snap rather than ease: the portal sends this to point the operator at
      // a hotspot they just clicked in the inspector, and a slow pan makes
      // "which dot is that?" take a second longer every time.
      this.controls?.setLook(yaw, pitch, { snap: true });
    });
  }

  private handleHotspotSelect(e: HotspotSelectEvent): void {
    // Edit mode short-circuit: route the click into the property editor
    // instead of running the type-specific switch (modals, navigation, etc.)
    // — operators tuning hotspots don't want their clicks teleporting them
    // to other rooms or opening lead-capture modals mid-edit.
    if (this.editorMode?.isActive() && this.editorPanel) {
      this.editorPanel.openFor(e.hotspot);
      const room = this.rooms?.getCurrent();
      if (room) this.embed?.notifyHotspotSelected(room.id, e.hotspot);
      this.analytics.log('hotspot_clicked', {
        hotspot: e.hotspot.id,
        label: e.hotspot.label,
        type: e.hotspot.type,
        source: e.source,
        editMode: true,
      });
      return;
    }
    const look = this.controls?.getLook();
    this.analytics.log('hotspot_clicked', {
      hotspot: e.hotspot.id,
      label: e.hotspot.label,
      type: e.hotspot.type,
      source: e.source,
      // Camera bearing at click time — lets PostHog answer "where were they
      // looking when they clicked this?" later, even though hotspot
      // positions are fixed.
      yaw: look ? Math.round(look.yaw * 10) / 10 : undefined,
      pitch: look ? Math.round(look.pitch * 10) / 10 : undefined,
      // Hotspot's own yaw/pitch from rooms.json — the H-dashboard plots
      // click markers on the gaze heatmap at THIS position so the rep can
      // see where buyer attention concentrated.
      hotspotYaw: e.hotspot.yaw,
      hotspotPitch: e.hotspot.pitch,
    });
    const hotspot = e.hotspot;
    // Visible feedback so the operator can confirm the click reached the handler
    // even if the resulting modal / room change happens to fail.
    showToast(`${hotspot.type} · ${hotspot.label}`);
    // Co-presence: broadcast modal-opening hotspot clicks so a guest in
    // 🎯 follow mode sees the same info / video / cta / surface popup the
    // host clicked. navigation/variant/note already replicate via the
    // 10 Hz scene broadcast (room + variant) — no extra event needed.
    // Suppressed when this very click was itself driven by remote-apply.
    if (
      !this.suppressActionBroadcast &&
      (hotspot.type === 'info' || hotspot.type === 'video' || hotspot.type === 'cta' || hotspot.type === 'surface')
    ) {
      this.copresence?.broadcastAction({ type: 'hotspot-open', hotspotId: hotspot.id });
    }
    try {
      switch (hotspot.type) {
        case 'navigation':
          this.handleNavigationHotspot(hotspot);
          break;
        case 'info':
          // In immersive-vr, HTML modals don't render — route to the 3D
          // in-scene info card instead. Outside VR, the existing modal flow
          // wins (richer typography + clickable links).
          if (this.xr?.isActive() && this.infoCard3D) {
            this.infoCard3D.show(hotspot);
            break;
          }
          if (!this.modal) {
            showToast('Erro: modal não inicializado', { tone: 'error' });
            break;
          }
          openInfoModal(this.modal, hotspot);
          this.controls?.setEnabled(false);
          break;
        case 'variant':
          if (this.rooms && this.variants) {
            const room = this.rooms.getCurrent();
            this.variants.setVariant(room, hotspot.targetVariantId, 'hotspot');
          }
          break;
        case 'video':
          if (!this.modal) {
            showToast('Erro: modal não inicializado', { tone: 'error' });
            break;
          }
          openVideoModal(this.modal, hotspot);
          this.controls?.setEnabled(false);
          break;
        case 'cta':
          if (!this.modal || !this.rooms) {
            showToast('Erro: modal/rooms não inicializado', { tone: 'error' });
            break;
          }
          openCtaModal(this.modal, hotspot, this.rooms.getCurrent(), this.config.cta);
          this.controls?.setEnabled(false);
          break;
        case 'surface':
          if (!this.materialPanel) {
            showToast('Erro: material panel não inicializado', { tone: 'error' });
            break;
          }
          this.openSurfacePanel(hotspot);
          // Don't disable look-around — designers want to drag-look while
          // experimenting with swatches.
          break;
        case 'note':
          this.openNoteEditor(hotspot);
          break;
        default: {
          // Exhaustiveness guard — TS will flag if a new hotspot type is added without handling.
          const _exhaustive: never = hotspot;
          void _exhaustive;
        }
      }
    } catch (err) {
      console.error('[app] hotspot handler threw', err);
      showToast(`Erro: ${(err as Error).message ?? err}`, { tone: 'error' });
    }
  }

  /**
   * Co-presence receive path for discrete UI actions. Looks up the hotspot
   * on the (already-synced) current room and replays the local click path
   * with broadcast suppressed so we don't echo back to the originator.
   *
   * Only invoked when the local user is in 🎯 follow mode AND the action
   * came from the followed peer (filter applied in Copresence).
   */
  private applyRemoteAction(input: RemoteActionInput): void {
    this.suppressActionBroadcast = true;
    try {
      if (input.type === 'hotspot-close') {
        this.modal?.close();
        return;
      }
      // hotspot-open: resolve the id against the current room. Variant +
      // room have already been mirrored via applyRemoteScene before any
      // hotspot click reaches us, so the lookup target is the synced room.
      const room = this.rooms?.getCurrent();
      if (!room) return;
      const hotspot = room.hotspots.find((h) => h.id === input.hotspotId);
      if (!hotspot) {
        // Hotspot not on the synced room — almost certainly a transient
        // race where the open arrived before the room/variant tick caught
        // up. Skip rather than crash; the next tick will re-converge.
        console.warn('[app] applyRemoteAction: hotspot not found on current room', input.hotspotId);
        return;
      }
      this.hotspots?.triggerSelect(hotspot, 'click');
    } finally {
      this.suppressActionBroadcast = false;
    }
  }

  /**
   * Surface hotspot dispatch. If the active renderer is a glTF model that
   * declares KHR_materials_variants, the panel opens populated with those
   * variant names and each pick performs a real PBR material swap. Otherwise
   * the panel falls back to the authored swatches on the hotspot (Sprint 1
   * emissive-tint path), so panorama rooms — and glTF rooms without variants
   * — keep working unchanged.
   */
  private openSurfacePanel(hotspot: SurfaceHotspot): void {
    if (!this.materialPanel) return;
    const variants = this.activeGltfVariants();
    const hasMaterials = !!(hotspot.maskUrl && hotspot.materials && hotspot.materials.length > 0);
    // Single-section short-circuit — preserve Sprint 1 look-and-feel when
    // there's no glTF variants AND no Phase 3 mask-overlay materials.
    if (variants.length === 0 && !hasMaterials) {
      this.materialPanel.open(hotspot, this.currentTintSwatchId);
      return;
    }
    // Compose sections dynamically. Order is fixed: tone (mood) → material
    // (Phase 3 mask repaint) → acabamento (glTF KHR variants), since each
    // step builds on the previous (tone composes with material/finish via
    // applyTint, then finish composes via the renderer's own state).
    const toneItems = hotspot.swatches.map((s) => ({
      id: s.id,
      label: s.label,
      dotColor: s.dotColor ?? rgbToCssTint(s.tint),
    }));
    const sections: Parameters<typeof this.materialPanel.openSections>[0]['sections'] = [];
    if (toneItems.length > 0) {
      sections.push({
        heading: 'Tonalizar',
        items: toneItems,
        currentId: this.currentTintSwatchId ?? toneItems[0]?.id ?? null,
        onPick: (item) => {
          const swatch = hotspot.swatches.find((s) => s.id === item.id);
          if (!swatch) return;
          this.materialTint = [swatch.tint[0], swatch.tint[1], swatch.tint[2]];
          this.currentTintSwatchId = swatch.id;
          this.applyTint();
          this.analytics.log('variant_changed', { kind: 'tint', swatch: swatch.id });
        },
      });
    }
    if (hasMaterials && hotspot.maskUrl && hotspot.materials) {
      // Lead with a "Original" pseudo-swatch so the buyer can always revert
      // to the bare panorama after experimenting. Same UI shape as a real
      // finish; the dotColor is a diagonal-line SVG so it reads as
      // "no overlay" without a separate icon.
      const RESET_ID = '__original__';
      const RESET_DOT =
        'url("data:image/svg+xml;utf8,<svg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 16 16\'>' +
        '<circle cx=\'8\' cy=\'8\' r=\'7\' fill=\'%23222\' stroke=\'%23555\' stroke-width=\'1\'/>' +
        '<line x1=\'3\' y1=\'13\' x2=\'13\' y2=\'3\' stroke=\'%23e84d4d\' stroke-width=\'2\'/></svg>") center / cover';
      const materialItems = [
        { id: RESET_ID, label: 'Original', dotColor: RESET_DOT },
        ...hotspot.materials.map((m) => ({ id: m.id, label: m.label, dotColor: m.dotColor })),
      ];
      const maskUrl = hotspot.maskUrl;
      const materials = hotspot.materials;
      // Cost estimator — only shows if the hotspot declares a surfaceAreaM2
      // AND every material finish has pricePerSqM set. Both are optional, so
      // surface hotspots without pricing data keep working unchanged.
      const surfaceArea = hotspot.surfaceAreaM2;
      const allHavePricing =
        materials.length > 0 && materials.every((m) => typeof m.pricePerSqM === 'number');
      const showPricing = surfaceArea !== undefined && allHavePricing;
      sections.push({
        heading: 'Material',
        items: materialItems,
        currentId: this.currentMaterialFinishId ?? RESET_ID,
        onPick: (item) => {
          if (item.id === RESET_ID) {
            this.currentMaterialFinishId = null;
            this.materialOverlay?.clear();
            this.analytics.log('variant_changed', { kind: 'material-finish', finish: 'original' });
            return;
          }
          const finish = materials.find((m) => m.id === item.id);
          if (!finish) return;
          this.currentMaterialFinishId = finish.id;
          this.ensureMaterialOverlay()?.apply({
            maskUrl,
            color: finish.color,
            ...(hotspot.shadingUrl ? { shadingUrl: hotspot.shadingUrl } : {}),
          }).catch((err) => {
            console.warn('[app] material overlay apply failed', err);
          });
          this.analytics.log('variant_changed', {
            kind: 'material-finish',
            finish: finish.id,
            ...(showPricing && finish.pricePerSqM !== undefined
              ? { totalBRL: Math.round((surfaceArea ?? 0) * finish.pricePerSqM) }
              : {}),
          });
        },
        footer: showPricing
          ? (currentId) => {
              if (!currentId || currentId === RESET_ID) {
                return `Área: ${formatArea(surfaceArea)} · escolha um acabamento para ver o custo`;
              }
              const finish = materials.find((m) => m.id === currentId);
              if (!finish || finish.pricePerSqM === undefined) return null;
              const total = surfaceArea * finish.pricePerSqM;
              return `${finish.label}: ${formatArea(surfaceArea)} × ${formatBRL(finish.pricePerSqM)}/m² = ${formatBRL(total)}`;
            }
          : undefined,
      });
    }
    if (variants.length > 0) {
      const variantItems = variants.map((name) => ({
        id: name,
        label: humanizeVariantName(name),
        dotColor: colorForName(name),
      }));
      sections.push({
        heading: 'Acabamento',
        items: variantItems,
        currentId: this.currentMaterialVariant,
        onPick: (item) => {
          this.currentMaterialVariant = item.id;
          this.gltfRenderer?.applyMaterialVariant(item.id);
          this.applyTint();
          this.analytics.log('variant_changed', { kind: 'material-variant', variant: item.id });
        },
      });
    }
    this.materialPanel.openSections({
      title: hotspot.label,
      description: hotspot.description ?? 'Tonalize, repinte ou troque o acabamento',
      sections,
    });
  }

  /**
   * Lazy MaterialOverlay accessor — instantiates the overlay the first time
   * a buyer touches a Phase 3 material finish. Returns null only if the
   * PlayCanvas app handle hasn't been wired yet (shouldn't happen in
   * practice — surface hotspots can't render before start() completes).
   */
  private ensureMaterialOverlay(): MaterialOverlay | null {
    if (this.materialOverlay) return this.materialOverlay;
    if (!this.appHandle) return null;
    this.materialOverlay = new MaterialOverlay(this.appHandle.app);
    return this.materialOverlay;
  }

  /**
   * Bridge between RoomManager and HotspotManager that injects buyer-
   * authored sticky notes into the per-room hotspot array. Every
   * setHotspots() call site routes through here so the runtime notes are
   * never forgotten on a room change or post-modal re-render.
   */
  private applyHotspotsForRoom(room: Room): void {
    if (!this.hotspots) return;
    const notes = this.notes?.forRoom(room.id) ?? [];
    if (notes.length === 0) {
      this.hotspots.setHotspots(room.hotspots);
    } else {
      this.hotspots.setHotspots([...room.hotspots, ...notes]);
    }
  }

  /**
   * 📝 Note placement + editor flow. The topbar button toggles placement
   * armed state; the next canvas tap drops a note at the picked yaw/pitch.
   * Clicking an existing note opens the editor modal instead of the
   * normal hotspot handler (the 'note' case in handleHotspotSelect).
   */
  private installNoteButton(): void {
    const btn = document.getElementById('note-button') as HTMLButtonElement | null;
    if (!btn) return;
    btn.addEventListener('click', () => {
      this.notePlacementArmed = !this.notePlacementArmed;
      btn.setAttribute('aria-pressed', String(this.notePlacementArmed));
      document.body.classList.toggle('is-placing-note', this.notePlacementArmed);
      showToast(
        this.notePlacementArmed
          ? 'Toque no ambiente para deixar uma nota'
          : 'Modo nota desativado',
        { tone: 'info' },
      );
    });
  }

  /** Drop a note at a yaw/pitch (called by the canvas-tap picker when
   *  placement is armed). Opens the editor immediately so the buyer
   *  types text right away. */
  private placeNoteAt(yaw: number, pitch: number): void {
    if (!this.rooms || !this.notes) return;
    const room = this.rooms.getCurrent();
    const note = this.notes.add(room.id, yaw, pitch);
    this.notePlacementArmed = false;
    document.body.classList.remove('is-placing-note');
    document.getElementById('note-button')?.setAttribute('aria-pressed', 'false');
    this.applyHotspotsForRoom(room);
    this.openNoteEditor(note);
  }

  /** Open the textarea modal for editing/deleting a note. */
  private openNoteEditor(note: NoteHotspot): void {
    if (!this.notes) return;
    const overlay = document.createElement('div');
    overlay.className = 'note-editor-overlay';
    const escapedText = note.text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    overlay.innerHTML = `
      <div class="note-editor" role="dialog" aria-label="Editar nota">
        <h3>Sua nota</h3>
        <textarea class="note-editor__text" placeholder="Ex: trocar bancada por mármore branco" maxlength="500">${escapedText}</textarea>
        <div class="note-editor__actions">
          <button type="button" class="note-editor__delete">Apagar</button>
          <button type="button" class="note-editor__cancel">Cancelar</button>
          <button type="button" class="note-editor__save">Salvar</button>
        </div>
      </div>
    `;
    document.body.appendChild(overlay);
    const textarea = overlay.querySelector('.note-editor__text') as HTMLTextAreaElement;
    textarea.focus();
    textarea.setSelectionRange(textarea.value.length, textarea.value.length);
    const room = this.rooms?.getCurrent();
    const close = (): void => overlay.remove();
    overlay.querySelector('.note-editor__cancel')?.addEventListener('click', close);
    overlay.querySelector('.note-editor__save')?.addEventListener('click', () => {
      this.notes?.update(note.id, textarea.value.trim());
      if (room) this.applyHotspotsForRoom(room);
      close();
    });
    overlay.querySelector('.note-editor__delete')?.addEventListener('click', () => {
      this.notes?.delete(note.id);
      if (room) this.applyHotspotsForRoom(room);
      close();
    });
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) close();
    });
  }

  /**
   * Returns the active glTF model's KHR_materials_variants names, or [] if
   * the current renderer isn't glTF or the model declares no variants.
   */
  private activeGltfVariants(): string[] {
    if (!this.gltfRenderer || this.currentRenderer !== this.gltfRenderer) return [];
    return this.gltfRenderer.getMaterialVariants();
  }

  private installCanvasTapPicker(): void {
    let downX = 0;
    let downY = 0;
    let downTime = 0;
    let downId = -1;
    // Edit-mode drag-to-reposition state. When non-null, pointermove rewrites
    // the hotspot's yaw/pitch and pointerup commits + closes the session.
    let draggingHotspot: Hotspot | null = null;
    this.canvas.addEventListener('pointerdown', (e: PointerEvent) => {
      downX = e.clientX;
      downY = e.clientY;
      downTime = performance.now();
      downId = e.pointerId;
      // In edit mode, a pointerdown on an existing hotspot arms a drag session.
      // We disable camera drag-look during the session so the panorama doesn't
      // slide while the operator is repositioning the marker.
      if (this.editorMode?.isActive() && !this.editorMode.isPlacementArmed() && this.hotspots) {
        const hit = this.hotspots.pickFromScreen(e.clientX, e.clientY);
        if (hit) {
          draggingHotspot = hit;
          this.controls?.setEnabled(false);
          document.body.classList.add('is-dragging-hotspot');
        }
      }
    });
    this.canvas.addEventListener('pointermove', (e: PointerEvent) => {
      if (!draggingHotspot) return;
      const placement = this.clientToYawPitch(e.clientX, e.clientY);
      if (!placement) return;
      draggingHotspot.yaw = Math.round(placement.yaw * 10) / 10;
      draggingHotspot.pitch = Math.round(placement.pitch * 10) / 10;
      // Re-spawn so the 3D dot + label follow the cursor in real time.
      const room = this.rooms?.getCurrent();
      if (room) this.applyHotspotsForRoom(room);
      // Keep the property panel's yaw/pitch inputs in sync if it's open on
      // this hotspot — pure visual feedback so the operator sees the numbers.
      this.editorPanel?.refreshFor(draggingHotspot);
    });
    const endDrag = (): void => {
      if (!draggingHotspot) return;
      draggingHotspot = null;
      this.controls?.setEnabled(true);
      document.body.classList.remove('is-dragging-hotspot');
      // Report on release, not on every pointermove: the intermediate yaws are
      // a gesture in progress, and the operator only means the one they let go on.
      this.notifyTourConfigChanged('hotspot-moved');
    };
    this.canvas.addEventListener('pointerup', (e: PointerEvent) => {
      const wasDragging = draggingHotspot !== null;
      endDrag();
      if (e.pointerId !== downId) return;
      const dx = Math.abs(e.clientX - downX);
      const dy = Math.abs(e.clientY - downY);
      const dt = performance.now() - downTime;
      // Tap = brief contact with minimal movement. Anything else is camera drag
      // (or, in edit mode, a hotspot reposition that already wrote yaw/pitch).
      if (dx > 8 || dy > 8 || dt > 600 || wasDragging) return;
      // Edit-mode placement intercepts the tap before measurement + picking.
      // When + Hotspot is armed, the next tap drops a new hotspot at the
      // picked yaw/pitch derived from the click coords + camera projection.
      if (this.editorMode?.isPlacementArmed()) {
        const placement = this.clientToYawPitch(e.clientX, e.clientY);
        if (placement) this.editorMode.placeHotspotAt(placement.yaw, placement.pitch);
        return;
      }
      // Note placement (📝 button armed) drops a buyer sticky at the picked
      // yaw/pitch and immediately opens the editor for the text.
      if (this.notePlacementArmed) {
        const placement = this.clientToYawPitch(e.clientX, e.clientY);
        if (placement) this.placeNoteAt(placement.yaw, placement.pitch);
        return;
      }
      // Measurement mode intercepts the tap before hotspot picking.
      if (this.measure?.isActive()) {
        this.measure.addPointAtClient(e.clientX, e.clientY);
        return;
      }
      if (!this.hotspots) return;
      const hit = this.hotspots.pickFromScreen(e.clientX, e.clientY);
      if (hit) this.hotspots.triggerSelect(hit, 'click');
    });
    this.canvas.addEventListener('pointercancel', endDrag);
    this.canvas.addEventListener('pointerleave', endDrag);
  }

  /**
   * Map a canvas-tap (client CSS pixels) to the yaw/pitch the user is aiming
   * at. Uses the camera's screenToWorld to ray-cast at HOTSPOT_DEFAULT_DISTANCE
   * then converts the world point back to spherical coords with vec3ToYawPitch.
   * Returns null if the camera isn't available.
   */
  private clientToYawPitch(clientX: number, clientY: number): { yaw: number; pitch: number } | null {
    const cam = this.appHandle?.camera.camera;
    if (!cam) return null;
    const rect = this.canvas.getBoundingClientRect();
    const cssX = clientX - rect.left;
    const cssY = clientY - rect.top;
    const worldScratch = new pc.Vec3();
    cam.screenToWorld(cssX, cssY, HOTSPOT_DEFAULT_DISTANCE, worldScratch);
    return vec3ToYawPitch(worldScratch.x, worldScratch.y, worldScratch.z);
  }

  /**
   * Builds the menu context, mounts the rail, and translates the static chrome
   * that is not owned by a feature module.
   *
   * Every entry is still "legacy" in this phase — MenuHost renders a button
   * carrying the id each module already looks up, and attaches no click handler
   * of its own, so the 19 modules keep working untouched while index.html loses
   * its hardcoded markup. Phase 3 moves them across one at a time.
   */
  private installMenu(): void {
    const menu = new MenuHost(this.menuContext());
    this.menu = menu;
    const operatorTools = operatorMenuItems({
      // Lazy on purpose: this runs before Dashboard and DebugLook are
      // constructed, and the ordering contract above is why it has to.
      toggleDashboard: () => this.dashboard?.toggle(),
      toggleDebugLook: () => this.debugLook?.toggle(),
    });
    const items = buildMenu([...legacyMenuItems(), ...operatorTools], this.menuContext());
    menu.mount(items);
    menu.setLocaleChangeHandler((locale) => this.setLocale(locale));
    // Built from the SAME item list the rail renders, in BOTH languages. In VR
    // no menu is drawn at all — MenuItem.vr and surfaces:'vr' are declared but
    // nothing reads them — so this index is the only route a headset visitor
    // has to Sound, Narration or the cinematic tour. Deriving it from `items`
    // rather than a hand-written list is what stops a new entry from working on
    // a phone and being unreachable in the headset.
    this.menuVoiceIndex = buildMenuVoiceIndex(items, ALL_DICTS);
    this.applyStaticChrome();
  }

  private menuContext(): MenuContext {
    return {
      audience: resolveAudience(),
      rooms: this.config.rooms,
      locale: this.locale,
      t: this.t,
      invalidate: () => this.menu?.refresh(),
      isXrActive: () => this.xr?.isActive() ?? false,
    };
  }

  /**
   * Switches language in place. Never reloads: in embed mode a reload discards
   * the operator's unsaved hotspot edits, which is the same reason the service
   * worker is skipped there (main.ts:79-86).
   */
  private setLocale(locale: Locale): void {
    if (locale === this.locale) return;
    this.locale = locale;
    this.t = getT(locale);
    setStoredLocale(locale);
    applyDocumentLocale(locale);
    this.menu?.setContext(this.menuContext());
    this.applyStaticChrome();
    // Chrome is only half of what the buyer reads. Room names, variant chips,
    // hotspot labels and the floor plan all come from the config, so rewrite
    // those strings and re-render the surfaces that cached them.
    applyLocale(this.localizedConfig, locale);
    this.rerenderLocalizedData();
  }

  /**
   * Pushes freshly localised config strings into the views that hold copies.
   *
   * Deliberately not a room transition: handleRoomSelect fades the screen and
   * re-emits an analytics event, and a language switch is neither a navigation
   * nor something worth recording as one. These are the four surfaces that read
   * a name, a label or a subtitle out of the config and keep it.
   */
  private rerenderLocalizedData(): void {
    const room = this.rooms?.getCurrent();
    if (!room || !this.ui || !this.variants) return;
    this.ui.floorplan.setRooms(this.config.rooms, this.config.defaultRoomId, this.config.features ?? []);
    this.ui.setRoom(room, this.variants.getCurrent(room).id);
    this.applyHotspotsForRoom(room);
    this.dollhouse?.setCurrentRoom(room.id);
  }

  /** Chrome text that no feature module owns, so nothing else will overwrite it. */
  private applyStaticChrome(): void {
    const eyebrow = document.querySelector('.topbar__eyebrow');
    if (eyebrow) eyebrow.textContent = this.t('topbar.eyebrow');

    const variants = document.getElementById('variant-panel');
    variants?.setAttribute('aria-label', this.t('bottombar.variantsAria'));

    const beforeAfter = document.getElementById('beforeafter-button');
    if (beforeAfter) beforeAfter.textContent = this.t('bottombar.beforeAfter');

    const compare = document.getElementById('compare-button');
    if (compare) {
      compare.textContent = `🔀 ${this.t('bottombar.compare')}`;
      compare.title = this.t('bottombar.compareTitle');
    }
  }

  private installFloorplanToggle(): void {
    const btn = document.getElementById('floorplan-toggle') as HTMLButtonElement | null;
    if (!btn || !this.ui) return;
    const PREF_KEY = 'imersivo:floorplanVisible';
    let visible = this.prefVisible(PREF_KEY, this.floorplanDefaultVisible());
    const apply = (): void => {
      if (visible) this.ui?.floorplan.show();
      else this.ui?.floorplan.hide();
      btn.setAttribute('aria-pressed', String(visible));
      btn.classList.toggle('is-active', visible);
    };
    apply();
    btn.addEventListener('click', () => {
      visible = !visible;
      localStorage.setItem(PREF_KEY, visible ? '1' : '0');
      apply();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'f' && e.key !== 'F') return;
      if (e.target && (e.target as HTMLElement).matches('input, textarea, [contenteditable]')) return;
      btn.click();
    });
  }

  private installDollhouseToggle(): void {
    if (!this.dollhouse) return;
    const btn = document.getElementById('dollhouse-toggle') as HTMLButtonElement | null;
    const PREF_KEY = 'imersivo:dollhouseVisible';
    // Default follows the data — see dollhouseDefaultVisible(). A config with
    // no floorplan polygons has nothing to render, so the widget starts off.
    let visible = this.prefVisible(PREF_KEY, this.dollhouseDefaultVisible());
    const apply = (): void => {
      this.dollhouse?.setVisible(visible);
      btn?.setAttribute('aria-pressed', String(visible));
      btn?.classList.toggle('is-active', visible);
    };
    // Applied BEFORE the button is required, and this order is the whole fix.
    // The Dollhouse paints from construction, so this call is the only thing
    // that ever turns it OFF at boot. Returning early on a missing button —
    // which is what a registry gate produces — left the maquete painted over
    // the panorama permanently, with nothing to dismiss it.
    apply();
    if (!btn) return;
    btn.addEventListener('click', () => {
      visible = !visible;
      localStorage.setItem(PREF_KEY, visible ? '1' : '0');
      apply();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'm' && e.key !== 'M') return;
      if (e.target && (e.target as HTMLElement).matches('input, textarea, [contenteditable]')) return;
      btn.click();
    });
  }

  /**
   * Wire #share-button. Builds a shareable URL pointed at the per-room
   * landing page produced by scripts/generate-og.mjs — that page carries the
   * room-specific OG meta, so link unfurlers in Slack / WhatsApp / iMessage
   * render the correct preview card. The landing page then JS-redirects to
   * the SPA's hash format so the recipient lands on exactly the same view.
   *
   * Keyboard shortcut: L (consistent with F/M/S/D/H single-letter overlays).
   */
  private installShareButton(): void {
    const btn = document.getElementById('share-button') as HTMLButtonElement | null;
    if (!btn) return;
    const copy = async (): Promise<void> => {
      const url = this.buildShareUrl();
      try {
        await navigator.clipboard.writeText(url);
        showToast('Link copiado · com preview OG');
      } catch {
        // Some browsers (or insecure contexts) block clipboard. Fall back to
        // a transient prompt-style toast so the operator can copy manually.
        showToast(`Copie: ${url}`, { tone: 'info' });
      }
      this.analytics.log('share_link_copied', {
        room: this.rooms?.getCurrent().id,
      });
    };
    btn.addEventListener('click', () => {
      void copy();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'l' && e.key !== 'L') return;
      if (e.target && (e.target as HTMLElement).matches('input, textarea, [contenteditable]')) return;
      void copy();
    });
  }

  /**
   * 📲 WhatsApp share — Brazil is WhatsApp-first; this is the cheapest
   * possible "send a tour" surface. On mobile, opens the native WhatsApp
   * app via the `whatsapp://send` scheme; on desktop, falls back to
   * web.whatsapp.com via the `https://wa.me/` permalink. The message body
   * is pre-filled in pt-BR with the buyer's saved-session URL if there's
   * an active session code (so the recipient lands on the saved state),
   * otherwise the room landing page URL with current camera/variant/sun.
   */
  private installWhatsAppButton(): void {
    const btn = document.getElementById('whatsapp-button') as HTMLButtonElement | null;
    if (!btn) return;
    btn.addEventListener('click', () => {
      const url = this.buildShareUrlForChat();
      const room = this.rooms?.getCurrent();
      const roomName = room?.name ?? 'apartamento';
      const message = `Veja este ${roomName.toLowerCase()} em 3D — explore por aqui: ${url}`;
      const encoded = encodeURIComponent(message);
      // wa.me works on every platform: mobile deep-links to the app,
      // desktop opens web.whatsapp.com. Avoids needing a separate
      // whatsapp:// scheme fork.
      const waUrl = `https://wa.me/?text=${encoded}`;
      window.open(waUrl, '_blank', 'noopener,noreferrer');
      this.analytics.log('whatsapp_share_opened', { room: room?.id });
    });
  }

  /**
   * URL for chat-share surfaces (WhatsApp, future SMS/Email). Prefers the
   * saved-session `?s=<code>` form when one exists so the recipient lands
   * on the buyer's exact saved state. Falls back to buildShareUrl() (the
   * /r/<roomId>/ landing form) when no session has been saved yet.
   */
  /**
   * Build the minimal rooms-context block to send with each /api/chat
   * request. Includes the current room/variant the buyer is looking at
   * plus a brief summary of other rooms in the tour so Claude can answer
   * navigation questions ("o que tem na cozinha?") without hallucinating.
   * Recomputed per chat turn (not cached) so material picks + variant
   * swaps are reflected in real time.
   */
  private buildChatContext(): ChatContext {
    const room = this.rooms?.getCurrent();
    const variant = room ? this.variants?.getCurrent(room) : null;
    const otherRooms = (this.config.rooms ?? [])
      .filter((r) => r.id !== room?.id)
      .map((r) => ({
        id: r.id,
        name: r.name,
        subtitle: r.subtitle,
        description: r.narration?.slice(0, 200),
        ceilingHeightM: r.ceilingHeightM,
        areaM2: computePolygonAreaM2(r.floorplan?.shape),
        variants: r.variants.filter((v) => !v.isBefore).map((v) => v.name).slice(0, 8),
      }));

    // Pull out the structured info from the active room that buyers actually
    // ask about: fixed features, material catalog with prices, and the
    // info-hotspot descriptions (which name the actual fixtures — sofa,
    // boiserie, marcenaria — and give the assistant something concrete to
    // quote instead of paraphrasing the narration).
    const currentFeatures = room?.features?.slice(0, 12);
    const currentInfoPoints: Array<{ label: string; description: string }> = [];
    const currentMaterials: ChatContext['currentMaterials'] = [];
    if (room) {
      for (const h of room.hotspots) {
        if (h.type === 'info' && h.description) {
          currentInfoPoints.push({ label: h.label, description: h.description });
        } else if (h.type === 'surface' && h.materials && h.materials.length > 0) {
          currentMaterials.push({
            surfaceLabel: h.label,
            surfaceAreaM2: h.surfaceAreaM2,
            finishes: h.materials.map((m) => ({
              label: m.label,
              pricePerSqM: m.pricePerSqM,
            })),
          });
        }
      }
    }

    return {
      currentRoomId: room?.id ?? 'unknown',
      currentRoomName: room?.name,
      currentRoomSubtitle: room?.subtitle,
      currentVariantName: variant?.name,
      currentRoomDescription: room?.narration?.slice(0, 500),
      currentRoomCeilingM: room?.ceilingHeightM,
      currentRoomAreaM2: computePolygonAreaM2(room?.floorplan?.shape),
      currentRoomWindowsM2: room?.windowsM2,
      currentRoomSolarOrientation: room?.solarOrientation,
      currentFeatures,
      currentInfoPoints,
      currentMaterials,
      currentVariantOptions: room?.variants.filter((v) => !v.isBefore).map((v) => v.name).slice(0, 8) ?? [],
      otherRooms,
      branding: { name: this.config.branding?.name, subtitle: this.config.branding?.subtitle },
      apartment: this.config.apartment,
      faq: this.config.faq?.slice(0, 8),
      cta: { contactUrl: this.config.cta?.contactUrl, defaultLabel: this.config.cta?.defaultLabel },
    };
  }

  /**
   * 📄 Download tour report — generates a one-page branded PDF
   * summarizing the buyer's visit (per-room variants, sticky notes,
   * active material finish + cost subtotal, hotspot attention).
   * Fires client-side via jspdf — no backend round-trip; private to
   * the buyer's device.
   */
  private installReportButton(): void {
    const btn = document.getElementById('report-button') as HTMLButtonElement | null;
    if (!btn) return;
    btn.addEventListener('click', async () => {
      try {
        // Dynamic import so jspdf + html2canvas (~130 KB gzipped) only
        // load when the buyer actually wants a PDF. Saves cold-start
        // bandwidth on every other session.
        const { downloadTourReport } = await import('../ui/tourReport');
        downloadTourReport(this.buildTourReportData());
        showToast('Relatório PDF baixado', { tone: 'info' });
        this.analytics.log('share_link_copied', { kind: 'pdf-report' });
      } catch (err) {
        console.error('[app] tour report generation failed', err);
        showToast('Falha ao gerar PDF', { tone: 'error' });
      }
    });
  }

  /**
   * Collect everything the PDF report needs into a flat structure. Called
   * fresh on every button click so the report reflects the buyer's
   * exact current state (variants, notes, material pick).
   */
  private buildTourReportData(): TourReportData {
    const variantsByRoom: Record<string, Variant | null> = {};
    for (const room of this.config.rooms) {
      variantsByRoom[room.id] = this.variants?.getCurrent(room) ?? null;
    }

    // Reconstruct the active material finish — currentMaterialFinishId is a
    // global App-level pointer; resolve it against whichever surface hotspot
    // (across all rooms) declares that finish id. First match wins.
    let currentMaterialFinish: TourReportData['currentMaterialFinish'];
    if (this.currentMaterialFinishId) {
      outer: for (const room of this.config.rooms) {
        for (const hotspot of room.hotspots) {
          if (hotspot.type !== 'surface' || !hotspot.materials) continue;
          const finish = hotspot.materials.find((m) => m.id === this.currentMaterialFinishId);
          if (finish) {
            currentMaterialFinish = {
              hotspotLabel: `${room.name} — ${hotspot.label}`,
              finish,
              surfaceAreaM2: hotspot.surfaceAreaM2,
            };
            break outer;
          }
        }
      }
    }

    // Aggregate hotspot clicks across the session ring buffer.
    const events = this.analytics.getAll();
    const clickCounts = new Map<string, { roomId: string; label: string; count: number }>();
    let firstTs = Infinity;
    let lastTs = 0;
    let currentRoom: string | null = null;
    for (const e of events) {
      if (e.ts < firstTs) firstTs = e.ts;
      if (e.ts > lastTs) lastTs = e.ts;
      const payload = e.payload as { room?: string; hotspot?: string; label?: string } | undefined;
      if (e.name === 'room_viewed' && payload?.room) currentRoom = payload.room;
      if (e.name === 'hotspot_clicked' && currentRoom && payload?.hotspot) {
        const key = `${currentRoom}:${payload.hotspot}`;
        const existing = clickCounts.get(key);
        if (existing) {
          existing.count += 1;
          if (payload.label) existing.label = payload.label;
        } else {
          clickCounts.set(key, {
            roomId: currentRoom,
            label: payload.label ?? payload.hotspot,
            count: 1,
          });
        }
      }
    }
    const hotspotClicks = Array.from(clickCounts.values()).sort((a, b) => b.count - a.count);
    const durationS = firstTs === Infinity ? 0 : Math.max(0, (lastTs - firstTs) / 1000);

    const code = this.savedSessions?.getCurrentCode() ?? null;
    const shareUrl = code
      ? `${window.location.origin}${window.location.pathname}?s=${code}`
      : null;

    return {
      config: this.config,
      notes: this.notes?.all() ?? [],
      variantsByRoom,
      currentMaterialFinish,
      hotspotClicks,
      session: {
        code,
        shareUrl,
        buyerNote: null, // BuyerSession.buyerNote isn't exposed via savedSessions today; future hook
        durationS,
      },
    };
  }

  private buildShareUrlForChat(): string {
    const code = this.savedSessions?.getCurrentCode();
    if (code) {
      const url = new URL(window.location.origin);
      url.searchParams.set('s', code);
      return url.toString();
    }
    return this.buildShareUrl();
  }

  /**
   * Construct the shareable URL for the current state. Targets the
   * /r/<roomId>/ landing page so link unfurlers fetch the room-specific OG
   * meta from the static HTML (the SPA's #hash format is invisible to them).
   * Other state goes in the search part of the URL — the landing JS
   * translates those into the hash format the SPA already understands.
   */
  private buildShareUrl(): string {
    const room = this.rooms?.getCurrent();
    if (!room) return window.location.href;
    const variant = this.variants?.getCurrent(room);
    const look = this.controls?.getLook();
    const sun = this.sun?.getState();
    const params = new URLSearchParams();
    if (variant) params.set('variant', variant.id);
    if (look) {
      params.set('yaw', wrapYawDeg(look.yaw).toFixed(1));
      params.set('pitch', look.pitch.toFixed(1));
    }
    if (sun) params.set('time', sun.hoursOfDay.toFixed(2));
    const qs = params.toString();
    const origin = window.location.origin;
    return `${origin}/r/${encodeURIComponent(room.id)}/${qs ? `?${qs}` : ''}`;
  }

  private installMeasureButton(): void {
    const btn = document.getElementById('measure-button') as HTMLButtonElement | null;
    if (!btn || !this.measure) return;
    btn.addEventListener('click', () => {
      this.measure?.toggleMode();
    });
    this.measure.on((update) => {
      btn.setAttribute('aria-pressed', String(update.isActive));
      btn.classList.toggle('is-active', update.isActive);
      const icon = btn.querySelector('.icon-button__icon');
      if (icon) {
        if (update.distanceM !== undefined) {
          icon.textContent = `📏 ${update.distanceM.toFixed(1)}m`;
          (icon as HTMLElement).style.fontSize = '11px';
        } else {
          icon.textContent = '📏';
          (icon as HTMLElement).style.fontSize = '';
        }
      }
      // Hide HTML hotspot labels while measuring so they don't intercept clicks.
      document.getElementById('hotspot-layer')?.classList.toggle('is-measuring', update.isActive);
    });
  }

  private async handleNavigationHotspot(hotspot: Hotspot & { type: 'navigation' }): Promise<void> {
    if (!this.rooms) return;
    const target = this.rooms.getById(hotspot.targetRoomId);
    if (!target) {
      console.warn(`[app] navigation hotspot ${hotspot.id} → unknown room ${hotspot.targetRoomId}`);
      return;
    }
    // Pass the override through by mutating a local copy of the room — small enough to be fine.
    const adjusted: Room = { ...target };
    if (hotspot.arriveYaw !== undefined) adjusted.initialYaw = hotspot.arriveYaw;
    if (hotspot.arrivePitch !== undefined) adjusted.initialPitch = hotspot.arrivePitch;
    // Camera dolly toward the door direction (~700ms), then the existing
    // fade-cycle handles the swap + arrival framing. Gives the navigation a
    // "walking-through" feel rather than a hard cut. Skip in edit mode so
    // authoring isn't slowed down.
    if (!this.editorMode?.isActive()) {
      await this.dollyTowards(hotspot.yaw, hotspot.pitch);
    }
    await this.handleRoomSelect(adjusted, 'navigation');
  }

  /**
   * Smooth camera "push" toward a target yaw/pitch with a subtle FOV narrow
   * for an inward-zoom feel. Snaps each frame (so the ease curve is driven
   * by this method, not the CameraControls damping). Disables user drag
   * while running so a stray pointermove can't interrupt mid-transition.
   * FOV is restored to the room's default in applyInitialCamera after the
   * fade, so the new room reads at normal framing.
   */
  private async dollyTowards(targetYaw: number, targetPitch: number): Promise<void> {
    if (!this.controls) return;
    const DURATION_MS = 700;
    const FOV_NARROW_DEG = 12;
    // Some hotspots in rooms.json carry steep pitch values used to anchor the
    // visible 3D dot in a specific spot of the panorama (e.g. to_kitchen has
    // pitch=-180 + a negative distance — the math wraps to put the dot at the
    // door). Without clamping, the dolly would then animate the camera toward
    // pitch=-180 → clamps to -85 → camera "points at the floor" mid-transition.
    // Clamp the dolly's *visible* pitch target to a natural look-around range
    // so the transition reads as "stepping toward the door," never "looking
    // straight down."
    const DOLLY_PITCH_LIMIT_DEG = 25;
    const clampedTargetPitch = Math.max(-DOLLY_PITCH_LIMIT_DEG, Math.min(DOLLY_PITCH_LIMIT_DEG, targetPitch));
    const start = this.controls.getLook();
    this.controls.setEnabled(false);
    const startTime = performance.now();
    await new Promise<void>((resolve) => {
      const step = (): void => {
        if (!this.controls) {
          resolve();
          return;
        }
        const t = Math.min(1, (performance.now() - startTime) / DURATION_MS);
        const e = easeOutCubic(t);
        const yaw = lerpAngleDeg(start.yaw, targetYaw, e);
        const pitch = lerp(start.pitch, clampedTargetPitch, e);
        const fov = lerp(start.fov, Math.max(30, start.fov - FOV_NARROW_DEG), e);
        this.controls.setLook(yaw, pitch, { snap: true });
        this.controls.setFov(fov, { snap: true });
        if (t < 1) requestAnimationFrame(step);
        else resolve();
      };
      requestAnimationFrame(step);
    });
    // Re-enable controls — the room change will snap to the arrival angle
    // mid-fade so the user never sees the dolly's final position lingering.
    this.controls.setEnabled(true);
  }

  // ── Rendering side effects ────────────────────────────────────

  private async swapPanorama(room: Room, variant: Variant, opts: { fade: boolean }): Promise<void> {
    if (!this.fade) return;
    if (opts.fade) {
      await this.fade.cycle(() => this.swapPanoramaImmediate(room, variant));
    } else {
      await this.swapPanoramaImmediate(room, variant);
    }
  }

  /**
   * Renderer dispatch. Picks panorama / gsplat / gltf based on the room+variant
   * config (defaults to panorama). Hides the previous renderer before showing
   * the next, then re-applies the active tint composition.
   */
  private async swapPanoramaImmediate(room: Room, variant: Variant): Promise<void> {
    const kind = rendererKindFor(room, variant);
    let next: RoomRenderer | null = null;
    if (kind === 'panorama') next = this.panoRenderer;
    else if (kind === 'gsplat') next = this.gsplatRenderer;
    else if (kind === 'gltf') next = this.gltfRenderer;
    if (!next) {
      console.warn(`[app] renderer "${kind}" unavailable; falling back to panorama`);
      next = this.panoRenderer;
    }
    if (!next) return;

    if (this.currentRenderer && this.currentRenderer !== next) {
      this.currentRenderer.hide();
    }
    this.currentRenderer = next;

    try {
      await next.show(room, variant);
    } catch (err) {
      console.error(`[app] renderer "${kind}" failed for ${room.id}/${variant.id}:`, err);
      // Recover: fall back to panorama if the chosen renderer threw (e.g.
      // missing splatUrl). Keeps the app usable when a config is incomplete.
      if (next !== this.panoRenderer && this.panoRenderer) {
        this.currentRenderer = this.panoRenderer;
        await this.panoRenderer.show(room, variant);
      }
    }

    this.applyTint();
  }

  /**
   * Push the composed (material × sun) tint into the active renderer. Called
   * after every renderer swap, every material-panel pick, and every sun
   * state change.
   */
  private applyTint(): void {
    if (!this.currentRenderer) return;
    this.currentRenderer.setTint(
      this.sunTint[0] * this.materialTint[0],
      this.sunTint[1] * this.materialTint[1],
      this.sunTint[2] * this.materialTint[2],
    );
  }

  /**
   * SunSystem listener — updates the sun-driven tint, pushes the new azimuth
   * into the dollhouse arrow, and re-composes the active tint. Time-of-day
   * is a global apartment-wide setting, so it persists across room changes.
   */
  private handleSunChange(state: SunState): void {
    this.sunTint = sunTintFor(state.elevationDeg);
    this.applyTint();
    this.dollhouse?.setSunAzimuth(state.azimuthDeg, state.elevationDeg);
    // Sun also drives the glTF renderer's directional key light so loaded
    // models relight as the time-of-day slider moves — visual parity with
    // the panorama's mood tint, but on the PBR path. Cached internally so
    // it applies even if the renderer hasn't built its lighting rig yet.
    this.gltfRenderer?.setSun(state);
  }

  private applyInitialCamera(room: Room): void {
    if (!this.controls) return;
    const yaw = room.initialYaw ?? 0;
    const pitch = room.initialPitch ?? 0;
    this.controls.setLook(yaw, pitch, { snap: true });
    // Snap FOV back to the default 72° — the dolly transition can leave it
    // narrowed mid-fade; restoring here makes the new room read at proper
    // framing regardless of how we arrived (navigation hotspot vs. minimap).
    this.controls.setFov(72, { snap: true });
  }
}

function waitOneFrame(): Promise<void> {
  return new Promise((resolve) => requestAnimationFrame(() => resolve()));
}

/**
 * Map solar elevation (degrees above horizon) to a per-channel emissive tint
 * that biases the panorama mood. Captures the rough daylight curve:
 *   • Deep night (elev < -6°): cool blue dim, ~0.45× luminance
 *   • Civil twilight (-6° → 0°): cooler indigo
 *   • Golden hour (0° → 15°): warm peach, ~1.1× luminance
 *   • Day (15° → 60°): near-neutral, slight warmth in the morning band
 *   • High sun (60° → 90°): faintly cool, slightly brighter
 * Smooth (not stepwise) so the time-of-day slider feels continuous.
 */
function sunTintFor(elevationDeg: number): [number, number, number] {
  const e = elevationDeg;
  if (e < -6) {
    // Deep night
    return [0.40, 0.45, 0.65];
  }
  if (e < 0) {
    // Civil twilight — interpolate night → horizon-warm
    const t = (e + 6) / 6;
    return [
      lerp(0.40, 0.85, t),
      lerp(0.45, 0.70, t),
      lerp(0.65, 0.85, t),
    ];
  }
  if (e < 15) {
    // Golden hour — interpolate horizon → mid-morning
    const t = e / 15;
    return [
      lerp(1.15, 1.05, t),
      lerp(0.88, 1.00, t),
      lerp(0.72, 0.95, t),
    ];
  }
  if (e < 60) {
    // Day — interpolate morning → noon
    const t = (e - 15) / 45;
    return [
      lerp(1.05, 1.00, t),
      lerp(1.00, 1.02, t),
      lerp(0.95, 1.05, t),
    ];
  }
  // High sun: faintly cool, slightly bright
  return [1.00, 1.02, 1.06];
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/** Cubic ease-out — fast start, slow finish. Used by the navigation dolly
 *  so the camera lunges toward the door, then settles. */
function easeOutCubic(t: number): number {
  const k = 1 - t;
  return 1 - k * k * k;
}

/** Lerp two angles in degrees through the shorter arc — avoids the 359°→0°
 *  long-way-around problem when navigating between yaws that straddle ±180. */
function lerpAngleDeg(from: number, to: number, t: number): number {
  let delta = ((to - from + 540) % 360) - 180;
  return from + delta * t;
}

/** "WhiteOakFloor" / "white_oak_floor" → "White Oak Floor". */
/**
 * Override map for KHR_materials_variants names where the raw GLB label is
 * jargon (e.g. "midnight" / "beach" / "street" on Khronos's
 * MaterialsVariantsShoe sample) and a finish-name reads better. Looked up
 * case-insensitively, with both Portuguese label and swatch-dot colour.
 */
const VARIANT_LABEL_OVERRIDES: Record<string, { label: string; dotColor: string }> = {
  midnight: { label: 'Couro Preto',     dotColor: '#1a1a22' },
  beach:    { label: 'Camurça Caramelo', dotColor: '#c79968' },
  street:   { label: 'Tecido Cinza',    dotColor: '#6c7079' },
};

function humanizeVariantName(raw: string): string {
  const override = VARIANT_LABEL_OVERRIDES[raw.toLowerCase()];
  if (override) return override.label;
  const spaced = raw
    .replace(/[_\-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim();
  if (!spaced) return raw;
  return spaced.replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Authored tint triples are linear-ish multipliers (e.g. 1.18 for warm). Compress
 *  to a 0-255 sRGB-ish dot preview that matches what the panel used to show in
 *  Sprint 1 — purely cosmetic, the actual tint is applied via setTint. */
function rgbToCssTint(rgb: [number, number, number]): string {
  const clamp = (v: number): number => Math.max(0, Math.min(255, Math.round(v * 200)));
  return `rgb(${clamp(rgb[0])}, ${clamp(rgb[1])}, ${clamp(rgb[2])})`;
}

/** Format a Brazilian Real amount with the local convention (R$ 18.400 or
 *  R$ 1.234.567 — period thousand separator, no decimal for whole-real
 *  amounts, comma decimal for fractional). Uses Intl.NumberFormat for
 *  correctness across locales but pins to pt-BR. */
function formatBRL(amount: number): string {
  const hasFraction = Math.abs(amount % 1) > 0.001;
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: hasFraction ? 2 : 0,
    maximumFractionDigits: hasFraction ? 2 : 0,
  }).format(amount);
}

/** Format square-meter areas with pt-BR convention: "24 m²" / "24,5 m²". */
function formatArea(m2: number): string {
  const hasFraction = Math.abs(m2 % 1) > 0.05;
  const formatted = hasFraction ? m2.toFixed(1).replace('.', ',') : String(Math.round(m2));
  return `${formatted} m²`;
}

/** Deterministic pastel HSL dot colour so variants look visually distinct.
 *  Falls back to a hash when no descriptive override is registered. */
function colorForName(name: string): string {
  const override = VARIANT_LABEL_OVERRIDES[name.toLowerCase()];
  if (override) return override.dotColor;
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) | 0;
  const hue = ((h % 360) + 360) % 360;
  return `hsl(${hue}, 45%, 55%)`;
}
